import { GRAVITY, ENEMY_SPEED, GROUND_HEIGHT, ROAD_TOP_MARGIN, WALKABLE_DEPTH, DEFAULT_GROUND_Y, DEPTH_MOVE_SPEED, getDrawY, getDepthScale } from './constants';
import { getEnemyPalette } from './levelGradients';
import soundManager from '../utils/SoundManager';

// Every level 1-20 gets a UNIQUE hand-crafted boss (P-2 boss hierarchy —
// Feb 2026). 20 distinct silhouettes, stats, and behaviors matching each
// level's theme. See BOSS_LORE in lore.js for names + lore blurbs.
export const getBossTypeForLevel = (level) => {
  if (level === 1) return 'boss_dog';           // Rustic city → IRON HOUND
  if (level === 2) return 'boss_alley';         // Neon alley → CHAIN LEADER
  if (level === 3) return 'boss_worm';          // Desert dunes → DUNE DEVOURER
  if (level === 4) return 'boss_highway';       // Desert highway → DESERT PROWLER
  if (level === 5) return 'boss_quarry';        // Cement quarry → CONCRETE COLOSSUS
  if (level === 6) return 'boss_factory';       // Factory → FACTORY FOREMAN
  if (level === 7) return 'boss_bridge';        // Wooden bridge → BRIDGE TROLL
  if (level === 8) return 'boss_kraken';        // Dock → DOCK KRAKEN
  if (level === 9) return 'boss_efreet';        // Volcanic → VOLCANIC EFREET
  if (level === 10) return 'boss_runner';       // Neon highway → NEON RUNNER PRIME
  if (level === 11) return 'boss_wraith';       // Arctic tundra → ARCTIC WRAITH
  if (level === 12) return 'boss_yeti';         // Snow mountains → SNOW YETI
  if (level === 13) return 'boss_vine';         // Jungle → JUNGLE VINE KING
  if (level === 14) return 'boss_toxin';        // Swamp → SWAMP TOXIN
  if (level === 15) return 'boss_sentinel';     // Industrial catwalk → STATION SENTINEL
  if (level === 16) return 'boss_cosmic';       // Space station → COSMIC WARLORD
  if (level === 17) return 'boss_castle';       // Castle → CASTLE WRAITH
  if (level === 18) return 'boss_phoenix';      // Ruined city → RUIN PHOENIX
  if (level === 19) return 'boss_crystal';      // Crystal cavern → CRYSTAL GUARDIAN
  return 'boss_omega';                           // Level 20 final → SHADOW OMEGA
};

// Monotonic enemy id counter for host-authoritative multiplayer sync
let _enemyIdCounter = 1;

export class Enemy {
  constructor(x, y, type = 'soldier', difficulty = 1, level = 1) {
    this.id = `e${_enemyIdCounter++}`;
    this.x = x;
    this.type = type;
    this.difficulty = difficulty;
    this.level = level;
    this.animFrame = 0;
    this.shootCooldown = 0;
    this.velocityX = -ENEMY_SPEED;
    this.isGrounded = true;
    this.jumpCooldown = 0;
    this.canJump = false;
    this.jumpChance = 0;
    this.isDead = false;
    this.markedForRemoval = false;
    this.onFire = false;
    this.fireTimer = 0;
    this.fireDamage = 0;
    
    // 2.5D depth: random spawn depth within walkable area
    this.groundY = Math.random() * WALKABLE_DEPTH;
    // Jump altitude (0 = on ground, positive = in air)
    this.jumpZ = 0;
    this.jumpVelocity = 0;
    // y will be computed in update() from groundY and jumpZ
    this.y = 0;
    
    const diffMultiplier = 1 + (difficulty - 1) * 0.25;
    
    switch (type) {
      case 'grunt':
        this.width = 30; this.height = 45; this.health = 15 * diffMultiplier;
        this.damage = 5; this.speed = 1.7; this.shootRate = 180; this.color = '#4A90D9';
        break;
      case 'runner':
        this.width = 28; this.height = 40; this.health = 10 * diffMultiplier;
        this.damage = 5; this.speed = 4.6; this.shootRate = 200; this.color = '#5AC8FA';
        this.canJump = true; this.jumpChance = 0.02;
        break;
      case 'jumper':
        this.width = 32; this.height = 48; this.health = 20 * diffMultiplier;
        this.damage = 12; this.speed = 2.9; this.shootRate = 140; this.color = '#FF9500';
        this.canJump = true; this.jumpChance = 0.08;
        break;
      case 'tank':
        this.width = 60; this.height = 40; this.health = 60 * diffMultiplier;
        this.damage = 15; this.speed = 1.4; this.shootRate = 150; this.color = '#3B8AC8';
        break;
      case 'commando':
        this.width = 38; this.height = 55; this.health = 45 * diffMultiplier;
        this.damage = 18; this.speed = 3.5; this.shootRate = 80; this.color = '#9945FF';
        this.canJump = true; this.jumpChance = 0.03;
        break;
      case 'mech':
        this.width = 50; this.height = 70; this.health = 100 * diffMultiplier;
        this.damage = 20; this.speed = 2.0; this.shootRate = 100; this.color = '#9945FF';
        break;
      case 'ninja':
        this.width = 30; this.height = 50; this.health = 30 * diffMultiplier;
        this.damage = 22; this.speed = 5.2; this.shootRate = 60; this.color = '#14F195';
        this.canJump = true; this.jumpChance = 0.1;
        break;
      case 'berserker':
        this.width = 45; this.height = 60; this.health = 80 * diffMultiplier;
        this.damage = 25; this.speed = 4.0; this.shootRate = 50; this.color = '#FF3B30';
        this.canJump = true; this.jumpChance = 0.05;
        break;
      case 'elite':
        this.width = 42; this.height = 58; this.health = 120 * diffMultiplier;
        this.damage = 20; this.speed = 3.2; this.shootRate = 70; this.color = '#FFD700';
        this.canJump = true; this.jumpChance = 0.04;
        break;
      case 'drone':
        this.width = 25; this.height = 25; this.health = 15 * diffMultiplier;
        this.damage = 5; this.speed = 5.2; this.shootRate = 60;
        this.flyHeight = 100 + Math.random() * 100; this.color = '#5AC8FA';
        break;
      case 'helicopter':
        this.width = 70; this.height = 35; this.health = 50 * diffMultiplier;
        this.damage = 12; this.speed = 4.0; this.shootRate = 90;
        this.flyHeight = 150; this.color = '#3B8AC8';
        break;
      case 'gunship':
        this.width = 80; this.height = 40; this.health = 80 * diffMultiplier;
        this.damage = 18; this.speed = 2.9; this.shootRate = 50;
        this.flyHeight = 130; this.color = '#9945FF';
        break;
      case 'sniper':
        // Slow, long-range, high damage. Stops to aim (aimTimer telegraph),
        // fires a single powerful shot, then repositions. Red laser sight
        // rendered by draw method during aim phase.
        this.width = 32; this.height = 52; this.health = 35 * diffMultiplier;
        this.damage = 30; this.speed = 1.2; this.shootRate = 200; this.color = '#3A3A5A';
        this.aimTimer = 0; this.aimDuration = 90; // 1.5s telegraph @ 60fps
        this.isAiming = false; this.sniperEngageRange = 550;
        break;
      case 'teleporter':
        // Vanishes every 2-3s, reappears ~200px closer to the player. Fades
        // in/out via teleportPhase: 0=solid, 1=fading-out, 2=invisible, 3=fading-in
        this.width = 30; this.height = 46; this.health = 25 * diffMultiplier;
        this.damage = 18; this.speed = 2.4; this.shootRate = 100; this.color = '#9945FF';
        this.teleportCooldown = 120 + Math.random() * 60; // 2-3s
        this.teleportPhase = 0; this.teleportAlpha = 1; this.teleportPhaseTimer = 0;
        break;
      case 'brute':
        // Walks slowly, telegraphs a charge (glows chargeColor for 30 frames),
        // then dashes at 3x normal speed for 60 frames. High health, high damage.
        this.width = 48; this.height = 62; this.health = 90 * diffMultiplier;
        this.damage = 22; this.speed = 1.8; this.shootRate = 300; this.color = '#8B4513';
        this.chargeCooldown = 180 + Math.random() * 120; // 3-5s
        this.chargePhase = 0; // 0=walking, 1=telegraphing, 2=dashing
        this.chargePhaseTimer = 0;
        this.baseSpeed = 1.8; // Restore-after-charge reference
        break;
      case 'boss': case 'boss_cyber':
        this.width = 120; this.height = 100; this.health = 500 * diffMultiplier;
        this.damage = 25; this.speed = 1.15; this.shootRate = 40;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'cyber'; this.bossName = 'CYBER OVERLORD';
        this.color = '#00FFFF'; this.secondaryColor = '#FF00FF';
        this.groundY = DEFAULT_GROUND_Y; // Bosses spawn at center
        break;
      case 'boss_urban':
        this.width = 130; this.height = 90; this.health = 600 * diffMultiplier;
        this.damage = 28; this.speed = 1.3; this.shootRate = 35;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'urban'; this.bossName = 'WAR MACHINE';
        this.color = '#4A5D23'; this.secondaryColor = '#8B7355';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_pixel':
        this.width = 100; this.height = 110; this.health = 550 * diffMultiplier;
        this.damage = 22; this.speed = 1.0; this.shootRate = 50;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'pixel'; this.bossName = 'PIXEL TITAN';
        this.color = '#FF6B6B'; this.secondaryColor = '#4ECDC4';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_industrial':
        this.width = 140; this.height = 95; this.health = 700 * diffMultiplier;
        this.damage = 30; this.speed = 0.9; this.shootRate = 45;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'industrial'; this.bossName = 'STEEL CRUSHER';
        this.color = '#8B4513'; this.secondaryColor = '#FFD700';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_inferno':
        this.width = 110; this.height = 120; this.health = 800 * diffMultiplier;
        this.damage = 35; this.speed = 1.4; this.shootRate = 30;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'inferno'; this.bossName = 'INFERNO TITAN';
        this.color = '#FF4500'; this.secondaryColor = '#FFD700';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_shadow':
        this.width = 150; this.height = 130; this.health = 1000 * diffMultiplier;
        this.damage = 40; this.speed = 1.2; this.shootRate = 25;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'shadow'; this.bossName = 'SHADOW COLOSSUS';
        this.color = '#1a1a2e'; this.secondaryColor = '#9945FF';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      // === P-2 Level-themed bosses (Feb 2026) ===================================
      case 'boss_dog': // L1 rustic city — IRON HOUND
        // Quadruped robotic guard dog. Twin red laser eyes (visual telegraph
        // when firing), charges the player + spits fireball projectiles.
        this.width = 110; this.height = 75; this.health = 450 * diffMultiplier;
        this.damage = 20; this.speed = 1.6; this.shootRate = 55;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'dog'; this.bossName = 'IRON HOUND';
        this.color = '#8B5A2B'; this.secondaryColor = '#4A2E1A';
        this.eyeGlow = '#FF3B30';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_alley': // L2 neon alley — CHAIN LEADER
        // Punk gang leader with a spinning chain-whip. Melee-focused; short
        // range but high damage windows when the whip crosses the player.
        this.width = 70; this.height = 100; this.health = 500 * diffMultiplier;
        this.damage = 26; this.speed = 2.0; this.shootRate = 50;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'alley'; this.bossName = 'CHAIN LEADER';
        this.color = '#FF00FF'; this.secondaryColor = '#00FFFF';
        this.chainAngle = 0; // radians, spins during combat for whip visual
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_worm': // L3 desert dunes — DUNE DEVOURER
        // Giant segmented sand worm. Periodically burrows (invisible), tracks
        // player x underground, erupts with a bite lunge. Big HP pool.
        this.width = 140; this.height = 90; this.health = 700 * diffMultiplier;
        this.damage = 35; this.speed = 1.4; this.shootRate = 90;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'worm'; this.bossName = 'DUNE DEVOURER';
        this.color = '#C2A366'; this.secondaryColor = '#8A6B2F';
        this.wormBurrowCooldown = 240; // ~4s between burrow cycles
        this.wormBurrowPhase = 0; // 0=above, 1=burrowing, 2=underground, 3=erupting
        this.wormPhaseTimer = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_highway': // L4 desert highway — DESERT PROWLER
        // Armored assault truck with a mounted turret. Fast lateral movement,
        // fires salvos of 3 bullets in quick succession.
        this.width = 160; this.height = 70; this.health = 600 * diffMultiplier;
        this.damage = 18; this.speed = 2.5; this.shootRate = 35;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'highway'; this.bossName = 'DESERT PROWLER';
        this.color = '#8A7A60'; this.secondaryColor = '#3A2818';
        this.salvoRemaining = 0; // Fires bullets in bursts of 3
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_quarry': // L5 cement quarry — CONCRETE COLOSSUS
        // Construction crane robot with a swinging wrecking ball. Slow but
        // heavy hits; wrecking ball swings on a ~1s cadence via ballAngle.
        this.width = 130; this.height = 130; this.health = 800 * diffMultiplier;
        this.damage = 32; this.speed = 1.1; this.shootRate = 75;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'quarry'; this.bossName = 'CONCRETE COLOSSUS';
        this.color = '#7A7A7A'; this.secondaryColor = '#3E3E3E';
        this.ballAngle = 0; // radians for wrecking-ball swing
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_factory': // L6 rust-belt factory — FACTORY FOREMAN
        this.width = 100; this.height = 120; this.health = 850 * diffMultiplier;
        this.damage = 28; this.speed = 1.3; this.shootRate = 60;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'factory'; this.bossName = 'FACTORY FOREMAN';
        this.color = '#FF6B00'; this.secondaryColor = '#3A1F0A';
        this.pressAngle = 0; // hydraulic press slam telegraph
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_bridge': // L7 wooden footbridge — BRIDGE TROLL
        this.width = 130; this.height = 130; this.health = 900 * diffMultiplier;
        this.damage = 35; this.speed = 1.0; this.shootRate = 80;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'bridge'; this.bossName = 'BRIDGE TROLL';
        this.color = '#4A6B2F'; this.secondaryColor = '#2E3A18';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_kraken': // L8 fishing dock — DOCK KRAKEN
        this.width = 180; this.height = 140; this.health = 950 * diffMultiplier;
        this.damage = 30; this.speed = 0.9; this.shootRate = 55;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'kraken'; this.bossName = 'DOCK KRAKEN';
        this.color = '#4A6B7C'; this.secondaryColor = '#1F3A4A';
        this.tentaclePhase = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_efreet': // L9 volcanic caldera — VOLCANIC EFREET
        this.width = 110; this.height = 140; this.health = 1000 * diffMultiplier;
        this.damage = 36; this.speed = 1.5; this.shootRate = 40;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'efreet'; this.bossName = 'VOLCANIC EFREET';
        this.color = '#FF4500'; this.secondaryColor = '#8B0000';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_runner': // L10 neon highway — NEON RUNNER PRIME
        this.width = 90; this.height = 115; this.health = 950 * diffMultiplier;
        this.damage = 24; this.speed = 2.8; this.shootRate = 25;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'runner'; this.bossName = 'NEON RUNNER PRIME';
        this.color = '#00FFFF'; this.secondaryColor = '#FF00FF';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_wraith': // L11 arctic tundra — ARCTIC WRAITH
        this.width = 100; this.height = 140; this.health = 1050 * diffMultiplier;
        this.damage = 32; this.speed = 1.6; this.shootRate = 50;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'wraith'; this.bossName = 'ARCTIC WRAITH';
        this.color = '#B0E0E6'; this.secondaryColor = '#4A6B8C';
        this.floatOffset = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_yeti': // L12 snow mountains — SNOW YETI
        this.width = 140; this.height = 150; this.health = 1150 * diffMultiplier;
        this.damage = 40; this.speed = 1.3; this.shootRate = 70;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'yeti'; this.bossName = 'SNOW YETI';
        this.color = '#F0F0F0'; this.secondaryColor = '#9DB4C0';
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_vine': // L13 jungle canopy — JUNGLE VINE KING
        this.width = 120; this.height = 150; this.health = 1200 * diffMultiplier;
        this.damage = 38; this.speed = 1.2; this.shootRate = 55;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'vine'; this.bossName = 'JUNGLE VINE KING';
        this.color = '#556B2F'; this.secondaryColor = '#7CFC00';
        this.vineWave = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_toxin': // L14 swamp — SWAMP TOXIN
        this.width = 105; this.height = 130; this.health = 1150 * diffMultiplier;
        this.damage = 34; this.speed = 1.4; this.shootRate = 45;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'toxin'; this.bossName = 'SWAMP TOXIN';
        this.color = '#6B7A2F'; this.secondaryColor = '#9ACD32';
        this.dripTimer = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_sentinel': // L15 industrial catwalk — STATION SENTINEL
        this.width = 150; this.height = 120; this.health = 1300 * diffMultiplier;
        this.damage = 30; this.speed = 1.1; this.shootRate = 35;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'sentinel'; this.bossName = 'STATION SENTINEL';
        this.color = '#5A5A5A'; this.secondaryColor = '#FF0000';
        this.searchlightAngle = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_cosmic': // L16 space station — COSMIC WARLORD
        this.width = 120; this.height = 150; this.health = 1400 * diffMultiplier;
        this.damage = 36; this.speed = 1.5; this.shootRate = 30;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'cosmic'; this.bossName = 'COSMIC WARLORD';
        this.color = '#4A2A6B'; this.secondaryColor = '#B0B0FF';
        this.starPulse = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_castle': // L17 castle — CASTLE WRAITH
        this.width = 100; this.height = 155; this.health = 1350 * diffMultiplier;
        this.damage = 42; this.speed = 1.3; this.shootRate = 65;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'castle'; this.bossName = 'CASTLE WRAITH';
        this.color = '#6B5A32'; this.secondaryColor = '#FFD700';
        this.swordAngle = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_phoenix': // L18 ruined city — RUIN PHOENIX
        this.width = 170; this.height = 140; this.health = 1450 * diffMultiplier;
        this.damage = 38; this.speed = 1.7; this.shootRate = 45;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'phoenix'; this.bossName = 'RUIN PHOENIX';
        this.color = '#8C3F1A'; this.secondaryColor = '#FF6347';
        this.wingBeat = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_crystal': // L19 crystal cavern — CRYSTAL GUARDIAN
        this.width = 130; this.height = 155; this.health = 1500 * diffMultiplier;
        this.damage = 40; this.speed = 1.2; this.shootRate = 50;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'crystal'; this.bossName = 'CRYSTAL GUARDIAN';
        this.color = '#8A2BE2'; this.secondaryColor = '#FF00FF';
        this.refractPulse = 0;
        this.groundY = DEFAULT_GROUND_Y;
        break;
      case 'boss_omega': // L20 hell arena — SHADOW OMEGA (final)
        this.width = 180; this.height = 170; this.health = 2000 * diffMultiplier;
        this.damage = 50; this.speed = 1.4; this.shootRate = 20;
        this.isBoss = true; this.phase = 1; this.attackPattern = 0;
        this.bossType = 'omega'; this.bossName = 'SHADOW OMEGA';
        this.color = '#1a0000'; this.secondaryColor = '#FFD700';
        this.omegaEyeAngles = [0, Math.PI / 3, Math.PI * 2 / 3, Math.PI, Math.PI * 4 / 3, Math.PI * 5 / 3];
        this.groundY = DEFAULT_GROUND_Y;
        break;
      // ==========================================================================
      default: // soldier
        this.width = 35; this.height = 50; this.health = 25 * diffMultiplier;
        this.damage = 10; this.speed = 2.6; this.shootRate = 120; this.color = '#5AC8FA';
        this.canJump = true; this.jumpChance = 0.015;
    }
    this.maxHealth = this.health;
    this.frozen = false;
    this.frozenTimer = 0;

    // Apply per-level palette overlay so enemies visually belong to their world.
    // Bosses store level palette separately (their phase-color logic in the
    // draw methods interpolates between themedPrimary and phase warning colors).
    const palette = getEnemyPalette(level);
    this.themedPrimary = palette.primary;
    this.themedSecondary = palette.secondary;
    this.themedAccent = palette.accent;
    if (this.isBoss) {
      // Boss keeps its unique base color for silhouette recognition, but the
      // per-level accent overlay tints armor plates + eye/detail glow.
      // (See drawBoss* methods — they consume themedSecondary/themedAccent
      // when non-null, falling back to hardcoded colors otherwise.)
    } else {
      // Regular enemies fully re-tint: body color inherits the palette primary,
      // with a subtle blend toward the type's original color so silhouettes
      // (ninja green vs elite gold vs berserker red) still telegraph danger.
      this.originalColor = this.color;
      this.color = this._blendColors(this.color, palette.primary, 0.55);
    }
  }

  // Blend two hex colors by t (0=colorA, 1=colorB). Used to preserve some of
  // the enemy's type-signature color while inheriting the level palette.
  _blendColors(colorA, colorB, t) {
    const parse = (c) => {
      const h = c.replace('#', '');
      return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
    };
    const [ra, ga, ba] = parse(colorA);
    const [rb, gb, bb] = parse(colorB);
    const r = Math.round(ra + (rb - ra) * t);
    const g = Math.round(ga + (gb - ga) * t);
    const b = Math.round(ba + (bb - ba) * t);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  isFlying() {
    return this.type === 'helicopter' || this.type === 'drone' || this.type === 'gunship';
  }

  update(playerX, playerGroundY, canvasHeight) {
    if (this.frozen) {
      this.frozenTimer--;
      if (this.frozenTimer <= 0) this.frozen = false;
      // Still compute y even when frozen
      if (this.isFlying()) {
        const targetY = canvasHeight - GROUND_HEIGHT - this.flyHeight;
        this.y += (targetY - this.y) * 0.05;
        this.y += Math.sin(Date.now() / 500) * 0.5;
      } else {
        this.y = getDrawY(canvasHeight, this.groundY, this.height, this.jumpZ);
      }
      return;
    }
    
    this.animFrame = (this.animFrame + 0.1) % 4;
    
    if (this.isFlying()) {
      // Flying enemies use legacy y positioning
      const targetY = canvasHeight - GROUND_HEIGHT - this.flyHeight;
      this.y += (targetY - this.y) * 0.05;
      this.y += Math.sin(Date.now() / 500) * 0.5;
      // Track player's depth loosely for rendering order
      const depthDiff = playerGroundY - this.groundY;
      if (Math.abs(depthDiff) > 5) {
        this.groundY += Math.sign(depthDiff) * 0.5;
      }
    } else {
      // Ground enemies: track player depth
      const depthDiff = playerGroundY - this.groundY;
      const depthTrackSpeed = this.isBoss ? 1.0 : (this.speed * 0.4);
      if (Math.abs(depthDiff) > 10) {
        this.groundY += Math.sign(depthDiff) * depthTrackSpeed;
      }
      this.groundY = Math.max(0, Math.min(WALKABLE_DEPTH, this.groundY));

      // Jump logic
      if (this.canJump && this.isGrounded && this.jumpCooldown <= 0) {
        if (Math.random() < this.jumpChance) {
          this.jumpVelocity = 10 + Math.random() * 4;
          this.isGrounded = false;
          this.jumpCooldown = 60 + Math.random() * 60;
        }
      }
      
      // Jump physics
      if (!this.isGrounded) {
        this.jumpVelocity -= GRAVITY * 0.8;
        this.jumpZ += this.jumpVelocity;
        if (this.jumpZ <= 0) {
          this.jumpZ = 0;
          this.jumpVelocity = 0;
          this.isGrounded = true;
        }
      }
      
      if (this.jumpCooldown > 0) this.jumpCooldown--;

      // Compute visual Y
      this.y = getDrawY(canvasHeight, this.groundY, this.height, this.jumpZ);
    }
    
    // Horizontal movement (X-axis)
    if (this.isBoss) {
      if (this.health < this.maxHealth * 0.3) { this.phase = 3; this.speed = 2.3; }
      else if (this.health < this.maxHealth * 0.6) { this.phase = 2; this.speed = 1.7; }

      // DUNE DEVOURER burrow cycle — 4s cycle: 3s above, 15f burrowing,
      // 60f underground tracking, 30f erupting/biting. Reduces velocity to 0
      // when underground so player has telegraph time. When erupting, snaps to
      // player x for the lunge attack.
      if (this.type === 'boss_worm') {
        this.wormPhaseTimer = (this.wormPhaseTimer || 0) + 1;
        if (this.wormBurrowPhase === 0) {
          // Above-ground: standard chase behavior
          const targetX = playerX + 100;
          this.velocityX = Math.abs(this.x - targetX) > 50 ? (this.x > targetX ? -this.speed : this.speed) : Math.sin(Date.now() / 1000) * this.speed;
          this.wormBurrowCooldown = (this.wormBurrowCooldown || 240) - 1;
          if (this.wormBurrowCooldown <= 0) { this.wormBurrowPhase = 1; this.wormPhaseTimer = 0; }
        } else if (this.wormBurrowPhase === 1) {
          // Burrowing down
          this.velocityX = 0;
          if (this.wormPhaseTimer >= 15) { this.wormBurrowPhase = 2; this.wormPhaseTimer = 0; }
        } else if (this.wormBurrowPhase === 2) {
          // Underground — track player x slowly
          this.velocityX = this.x > playerX ? -this.speed * 0.8 : this.speed * 0.8;
          if (this.wormPhaseTimer >= 60) {
            // Snap to player position for lunge
            this.x = playerX - this.width * 0.5;
            this.wormBurrowPhase = 3; this.wormPhaseTimer = 0;
          }
        } else if (this.wormBurrowPhase === 3) {
          // Erupting/biting — brief hitbox window
          this.velocityX = 0;
          if (this.wormPhaseTimer >= 30) {
            this.wormBurrowPhase = 0; this.wormPhaseTimer = 0;
            this.wormBurrowCooldown = 240; // Reset for next cycle
          }
        }
      } else {
        const targetX = playerX + 200;
        if (Math.abs(this.x - targetX) > 50) {
          this.velocityX = this.x > targetX ? -this.speed : this.speed;
        } else {
          this.velocityX = Math.sin(Date.now() / 1000) * this.speed;
        }
      }
    } else if (this.type === 'sniper') {
      // Sniper: stops moving when within engage range, aims, fires powerful shot.
      const distToPlayer = Math.abs(this.x - playerX);
      if (distToPlayer < this.sniperEngageRange && distToPlayer > 200) {
        // In engage range — halt, aim, fire
        this.velocityX = 0;
        this.isAiming = true;
        this.aimTimer++;
        if (this.aimTimer >= this.aimDuration && this.shootCooldown <= 0) {
          // Ready to fire; canShoot() gate + shoot() handled by engine
          this.aimTimer = 0;
        }
      } else {
        // Reposition
        this.isAiming = false;
        this.aimTimer = 0;
        const direction = this.x > playerX ? -1 : 1;
        this.velocityX = direction * this.speed;
      }
    } else if (this.type === 'teleporter') {
      // Teleporter: normal walk, then vanish → reappear closer to player
      this.teleportPhaseTimer++;
      if (this.teleportPhase === 0) {
        // Solid, walking
        const direction = this.x > playerX ? -1 : 1;
        this.velocityX = direction * this.speed;
        this.teleportAlpha = 1;
        this.teleportCooldown--;
        if (this.teleportCooldown <= 0) {
          this.teleportPhase = 1; this.teleportPhaseTimer = 0;
        }
      } else if (this.teleportPhase === 1) {
        // Fading out (15 frames)
        this.velocityX = 0;
        this.teleportAlpha = Math.max(0, 1 - this.teleportPhaseTimer / 15);
        if (this.teleportPhaseTimer >= 15) {
          // Teleport ~200px closer to player, capped so it doesn't overshoot
          const closer = 200;
          this.x = this.x > playerX ? this.x - closer : this.x + closer;
          this.groundY = Math.max(0, Math.min(WALKABLE_DEPTH, playerGroundY + (Math.random() - 0.5) * 20));
          this.teleportPhase = 2; this.teleportPhaseTimer = 0;
        }
      } else if (this.teleportPhase === 2) {
        // Fading back in (15 frames)
        this.velocityX = 0;
        this.teleportAlpha = Math.min(1, this.teleportPhaseTimer / 15);
        if (this.teleportPhaseTimer >= 15) {
          this.teleportPhase = 0; this.teleportPhaseTimer = 0;
          this.teleportCooldown = 120 + Math.random() * 60; // Next teleport in 2-3s
        }
      }
    } else if (this.type === 'brute') {
      // Brute: walks slowly, telegraphs charge (glows red 30f), dashes 3x (60f)
      this.chargePhaseTimer++;
      if (this.chargePhase === 0) {
        // Walking normally
        const direction = this.x > playerX ? -1 : 1;
        this.velocityX = direction * this.speed;
        this.chargeCooldown--;
        if (this.chargeCooldown <= 0 && Math.abs(this.x - playerX) < 600) {
          this.chargePhase = 1; this.chargePhaseTimer = 0;
        }
      } else if (this.chargePhase === 1) {
        // Telegraphing charge (glow via draw method)
        this.velocityX *= 0.7; // Slow down
        if (this.chargePhaseTimer >= 30) {
          this.chargePhase = 2; this.chargePhaseTimer = 0;
        }
      } else if (this.chargePhase === 2) {
        // Dashing at 3x speed
        const direction = this.x > playerX ? -1 : 1;
        this.velocityX = direction * this.baseSpeed * 3;
        if (this.chargePhaseTimer >= 60) {
          this.chargePhase = 0; this.chargePhaseTimer = 0;
          this.chargeCooldown = 180 + Math.random() * 120;
        }
      }
    } else {
      if (Math.abs(this.x - playerX) < 500) {
        const direction = this.x > playerX ? -1 : 1;
        this.velocityX = direction * this.speed;
      }
    }
    
    this.x += this.velocityX;
    if (this.shootCooldown > 0) this.shootCooldown--;
  }

  draw(ctx, cameraX, canvasHeight) {
    const screenX = this.x - cameraX;
    const drawY = this.y;
    
    ctx.save();
    
    if (this.frozen) {
      ctx.globalAlpha = 0.7;
      ctx.filter = 'hue-rotate(180deg) saturate(150%)';
    }
    // Teleporter fade — apply on top of frozen alpha if simultaneously frozen
    if (this.type === 'teleporter' && this.teleportAlpha !== undefined && this.teleportAlpha < 1) {
      ctx.globalAlpha = (ctx.globalAlpha || 1) * this.teleportAlpha;
    }
    
    // Draw shadow for ground enemies (before scaling so shadow stays at ground)
    if (!this.isFlying()) {
      const shadowAlpha = 0.25 - (this.jumpZ / 400);
      if (shadowAlpha > 0) {
        const depthScale = getDepthScale(this.groundY);
        ctx.globalAlpha = this.frozen ? 0.15 : shadowAlpha;
        ctx.fillStyle = '#000';
        ctx.beginPath();
        const shadowShrink = 1 - this.jumpZ / 500;
        const feetY = drawY + this.height;
        ctx.ellipse(screenX + this.width / 2, feetY + this.jumpZ, 
                     this.width * 0.35 * shadowShrink * depthScale, 4 * shadowShrink * depthScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = this.frozen ? 0.7 : 1;
      }
    }
    
    // Apply depth-based perspective scaling for the entity body
    const scale = getDepthScale(this.groundY);
    const cx = screenX + this.width / 2;
    const cy = drawY + this.height / 2;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);
    
    switch (this.type) {
      case 'tank': this.drawTank(ctx, screenX, drawY); break;
      case 'helicopter': this.drawHelicopter(ctx, screenX, drawY); break;
      case 'gunship': this.drawGunship(ctx, screenX, drawY); break;
      case 'mech': this.drawMech(ctx, screenX, drawY); break;
      case 'drone': this.drawDrone(ctx, screenX, drawY); break;
      case 'boss': case 'boss_cyber': this.drawBossCyber(ctx, screenX, drawY); break;
      case 'boss_urban': this.drawBossUrban(ctx, screenX, drawY); break;
      case 'boss_pixel': this.drawBossPixel(ctx, screenX, drawY); break;
      case 'boss_industrial': this.drawBossIndustrial(ctx, screenX, drawY); break;
      case 'boss_inferno': this.drawBossInferno(ctx, screenX, drawY); break;
      case 'boss_shadow': this.drawBossShadow(ctx, screenX, drawY); break;
      case 'boss_dog': this.drawBossDog(ctx, screenX, drawY); break;
      case 'boss_alley': this.drawBossAlley(ctx, screenX, drawY); break;
      case 'boss_worm': this.drawBossWorm(ctx, screenX, drawY); break;
      case 'boss_highway': this.drawBossHighway(ctx, screenX, drawY); break;
      case 'boss_quarry': this.drawBossQuarry(ctx, screenX, drawY); break;
      case 'boss_factory': this.drawBossFactory(ctx, screenX, drawY); break;
      case 'boss_bridge': this.drawBossBridge(ctx, screenX, drawY); break;
      case 'boss_kraken': this.drawBossKraken(ctx, screenX, drawY); break;
      case 'boss_efreet': this.drawBossEfreet(ctx, screenX, drawY); break;
      case 'boss_runner': this.drawBossRunner(ctx, screenX, drawY); break;
      case 'boss_wraith': this.drawBossWraith(ctx, screenX, drawY); break;
      case 'boss_yeti': this.drawBossYeti(ctx, screenX, drawY); break;
      case 'boss_vine': this.drawBossVine(ctx, screenX, drawY); break;
      case 'boss_toxin': this.drawBossToxin(ctx, screenX, drawY); break;
      case 'boss_sentinel': this.drawBossSentinel(ctx, screenX, drawY); break;
      case 'boss_cosmic': this.drawBossCosmic(ctx, screenX, drawY); break;
      case 'boss_castle': this.drawBossCastle(ctx, screenX, drawY); break;
      case 'boss_phoenix': this.drawBossPhoenix(ctx, screenX, drawY); break;
      case 'boss_crystal': this.drawBossCrystal(ctx, screenX, drawY); break;
      case 'boss_omega': this.drawBossOmega(ctx, screenX, drawY); break;
      case 'grunt': case 'runner': case 'jumper': case 'commando':
      case 'ninja': case 'berserker': case 'elite':
      case 'sniper': case 'teleporter': case 'brute':
        this.drawGenericEnemy(ctx, screenX, drawY); break;
      default: this.drawSoldier(ctx, screenX, drawY);
    }

    // Per-level boss aura — a soft themed color halo around the boss silhouette
    // so cyber boss reads as icy on L11-12 (arctic accent), industrial reads as
    // cosmic-purple on L15-16 (space station accent), etc. Uses the level's
    // enemy palette accent since it's the brightest, most identity-carrying hue.
    if (this.isBoss && this.themedAccent) {
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      const auraPulse = 0.35 + Math.sin(Date.now() / 400) * 0.15;
      ctx.shadowColor = this.themedAccent;
      ctx.shadowBlur = 30;
      ctx.strokeStyle = this.themedAccent;
      ctx.lineWidth = 2;
      ctx.globalAlpha = auraPulse;
      // Trace the full boss bounding box (accounting for extras like drawBossShadow tendrils)
      ctx.strokeRect(screenX - 8, drawY - 20, this.width + 16, this.height + 30);
      ctx.restore();
    }
    
    if (this.isBoss) {
      const barWidth = 350;
      const barX = screenX + this.width / 2 - barWidth / 2;
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(barX - 2, drawY - 32, barWidth + 4, 16);
      ctx.fillStyle = '#333';
      ctx.fillRect(barX, drawY - 30, barWidth, 12);
      const healthColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : (this.themedAccent || this.secondaryColor || '#9945FF');
      ctx.fillStyle = healthColor;
      ctx.fillRect(barX, drawY - 30, (this.health / this.maxHealth) * barWidth, 12);
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillRect(barX + barWidth * 0.3 - 1, drawY - 30, 2, 12);
      ctx.fillRect(barX + barWidth * 0.6 - 1, drawY - 30, 2, 12);
      ctx.shadowColor = this.color || '#9945FF';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px "Press Start 2P", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(this.bossName || 'BOSS', screenX + this.width / 2, drawY - 45);
      ctx.font = 'bold 8px "Press Start 2P", monospace';
      ctx.fillStyle = healthColor;
      ctx.fillText(`PHASE ${this.phase}`, screenX + this.width / 2, drawY - 55);
      ctx.shadowBlur = 0;
    } else if (this.health < this.maxHealth) {
      ctx.fillStyle = '#333';
      ctx.fillRect(screenX, drawY - 10, this.width, 4);
      ctx.fillStyle = '#FF3B30';
      ctx.fillRect(screenX, drawY - 10, (this.health / this.maxHealth) * this.width, 4);
    }
    
    if (this.frozen) {
      ctx.filter = 'none';
      ctx.globalAlpha = 0.4;
      ctx.fillStyle = '#00FFFF';
      ctx.fillRect(screenX - 2, drawY - 2, this.width + 4, this.height + 4);
      ctx.fillStyle = '#AAFFFF';
      for (let i = 0; i < 3; i++) {
        const crystalX = screenX + (this.width / 4) * (i + 0.5);
        const crystalY = drawY + Math.random() * this.height;
        ctx.beginPath();
        ctx.moveTo(crystalX, crystalY - 5);
        ctx.lineTo(crystalX + 3, crystalY);
        ctx.lineTo(crystalX, crystalY + 5);
        ctx.lineTo(crystalX - 3, crystalY);
        ctx.closePath();
        ctx.fill();
      }
    }
    
    ctx.restore();
  }

  // All boss drawing methods remain exactly the same (they use drawY passed to them)
  drawBossCyber(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : '#00FFFF';
    const legOffset = Math.sin(this.animFrame * Math.PI) * 8;
    ctx.shadowColor = phaseColor; ctx.shadowBlur = 20;
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(x + 15, y + 70 + legOffset, 25, 35);
    ctx.fillRect(x + 80, y + 70 - legOffset, 25, 35);
    ctx.fillStyle = phaseColor;
    ctx.fillRect(x + 18, y + 72 + legOffset, 3, 30);
    ctx.fillRect(x + 83, y + 72 - legOffset, 3, 30);
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(x + 10, y + 25, 100, 50);
    ctx.strokeStyle = phaseColor; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x + 20, y + 35); ctx.lineTo(x + 50, y + 35); ctx.lineTo(x + 50, y + 55); ctx.lineTo(x + 80, y + 55); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + 30, y + 65); ctx.lineTo(x + 90, y + 65); ctx.stroke();
    ctx.fillStyle = '#1a1a2e'; ctx.fillRect(x + 35, y, 50, 30);
    ctx.fillStyle = phaseColor; ctx.fillRect(x + 40, y + 8, 40, 12);
    const scanX = x + 42 + Math.sin(time * 3) * 15;
    ctx.fillStyle = '#fff'; ctx.fillRect(scanX, y + 10, 8, 8);
    ctx.fillStyle = '#1a1a2e';
    ctx.save(); ctx.translate(x + 10, y + 35); ctx.rotate(Math.sin(time * 2) * 0.2);
    ctx.fillRect(-20, -5, 25, 15); ctx.fillStyle = '#FF00FF'; ctx.fillRect(-30, -3, 15, 10);
    ctx.beginPath(); ctx.arc(-35, 2, 8, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 0, 255, ${0.5 + Math.sin(time * 5) * 0.3})`; ctx.fill(); ctx.restore();
    ctx.save(); ctx.translate(x + 110, y + 35); ctx.rotate(-Math.sin(time * 2) * 0.2);
    ctx.fillStyle = '#1a1a2e'; ctx.fillRect(-5, -5, 25, 15); ctx.fillStyle = '#FF00FF'; ctx.fillRect(15, -3, 15, 10);
    ctx.beginPath(); ctx.arc(35, 2, 8, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 0, 255, ${0.5 + Math.sin(time * 5) * 0.3})`; ctx.fill(); ctx.restore();
    ctx.strokeStyle = phaseColor; ctx.lineWidth = 2; ctx.strokeRect(x + 5, y - 5, 110, 85); ctx.shadowBlur = 0;
  }

  drawBossUrban(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : '#4A5D23';
    ctx.fillStyle = '#333';
    ctx.fillRect(x + 5, y + 70, 50, 25); ctx.fillRect(x + 75, y + 70, 50, 25);
    for (let i = 0; i < 5; i++) { ctx.fillStyle = '#555'; ctx.fillRect(x + 8 + i * 10 + (this.animFrame * 3) % 10, y + 75, 6, 15); ctx.fillRect(x + 78 + i * 10 + (this.animFrame * 3) % 10, y + 75, 6, 15); }
    ctx.fillStyle = phaseColor; ctx.fillRect(x + 15, y + 20, 100, 55);
    ctx.fillStyle = '#3A4A1A'; ctx.fillRect(x + 20, y + 25, 90, 10); ctx.fillRect(x + 20, y + 55, 90, 10);
    ctx.fillStyle = '#4A5D23'; ctx.fillRect(x + 40, y - 5, 50, 30);
    ctx.fillStyle = '#333'; ctx.fillRect(x + 90, y + 5, 40, 12);
    ctx.fillStyle = '#FF4444'; ctx.fillRect(x + 125, y + 7, 8, 8);
    ctx.fillStyle = '#333'; ctx.fillRect(x - 5, y + 30, 20, 8); ctx.fillRect(x + 115, y + 30, 20, 8);
    ctx.fillStyle = '#FFD700'; for (let i = 0; i < 4; i++) { ctx.fillRect(x + 25 + i * 25, y + 68, 10, 3); }
    if (this.phase >= 2) { ctx.fillStyle = `rgba(100,100,100,${0.3+Math.sin(time*4)*0.2})`; for (let i=0;i<3;i++) { ctx.beginPath(); ctx.arc(x+60+Math.sin(time+i)*10,y-10-i*8,6+i*2,0,Math.PI*2); ctx.fill(); } }
  }

  drawBossPixel(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : '#FF6B6B';
    const legOffset = Math.sin(this.animFrame * Math.PI) * 10;
    const pixelSize = 8;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#4ECDC4';
    for (let py = 0; py < 4; py++) { ctx.fillRect(x+15,y+75+py*pixelSize+legOffset,pixelSize*2,pixelSize); ctx.fillRect(x+70,y+75+py*pixelSize-legOffset,pixelSize*2,pixelSize); }
    ctx.fillStyle = phaseColor;
    for (let py=0;py<5;py++) { for (let px=0;px<10;px++) { if ((px+py)%3!==0) ctx.fillRect(x+10+px*pixelSize,y+30+py*pixelSize,pixelSize-1,pixelSize-1); } }
    ctx.fillStyle = '#FFE66D'; ctx.fillRect(x+42,y+45,pixelSize*2,pixelSize*2);
    ctx.fillStyle = `rgba(255,230,109,${0.5+Math.sin(time*3)*0.3})`; ctx.fillRect(x+38,y+41,pixelSize*3,pixelSize*3);
    ctx.fillStyle = '#4ECDC4'; ctx.fillRect(x+30,y,pixelSize*5,pixelSize*4);
    ctx.fillStyle = this.phase===3?'#FF0000':'#fff'; ctx.fillRect(x+35,y+10,pixelSize,pixelSize); ctx.fillRect(x+55,y+10,pixelSize,pixelSize);
    if (this.phase===3) { ctx.fillStyle='#000'; ctx.fillRect(x+33,y+8,pixelSize+4,3); ctx.fillRect(x+55,y+8,pixelSize+4,3); }
    ctx.fillStyle = '#4ECDC4';
    const armExtend = this.phase>=2?Math.sin(time*2)*10:0;
    ctx.fillRect(x-10-armExtend,y+35,pixelSize*3,pixelSize*2); ctx.fillRect(x+85+armExtend,y+35,pixelSize*3,pixelSize*2);
    ctx.fillStyle = phaseColor; ctx.fillRect(x-18-armExtend,y+33,pixelSize*2,pixelSize*3); ctx.fillRect(x+100+armExtend,y+33,pixelSize*2,pixelSize*3);
  }

  drawBossIndustrial(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase===3?'#FF3B30':this.phase===2?'#FF9500':'#8B4513';
    const legOffset = Math.sin(this.animFrame*Math.PI)*5;
    ctx.fillStyle = '#555'; ctx.fillRect(x+20,y+65+legOffset,30,35); ctx.fillRect(x+90,y+65-legOffset,30,35);
    ctx.fillStyle = '#FFD700'; ctx.fillRect(x+28,y+68+legOffset,6,25+Math.sin(time*5)*3); ctx.fillRect(x+98,y+68-legOffset,6,25+Math.sin(time*5)*3);
    ctx.fillStyle = phaseColor; ctx.fillRect(x+10,y+15,120,55);
    ctx.fillStyle = '#666'; for(let i=0;i<5;i++){ctx.beginPath();ctx.arc(x+20+i*25,y+20,4,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+20+i*25,y+65,4,0,Math.PI*2);ctx.fill();}
    ctx.fillStyle = '#666'; ctx.fillRect(x+35,y-15,70,35);
    ctx.fillStyle = '#333'; for(let i=0;i<5;i++){ctx.beginPath();ctx.moveTo(x+45+i*12,y+20);ctx.lineTo(x+51+i*12,y+30);ctx.lineTo(x+39+i*12,y+30);ctx.fill();}
    ctx.fillStyle = this.phase>=2?`rgba(255,0,0,${0.5+Math.sin(time*8)*0.5})`:'#FF0000'; ctx.beginPath();ctx.arc(x+70,y-5,8,0,Math.PI*2);ctx.fill();
    ctx.strokeStyle='#666';ctx.lineWidth=8;ctx.beginPath();ctx.moveTo(x,y+30);ctx.lineTo(x-30,y+10+Math.sin(time)*10);ctx.stroke();
    ctx.beginPath();ctx.moveTo(x+140,y+30);ctx.lineTo(x+170,y+10+Math.sin(time+1)*10);ctx.stroke();
    ctx.fillStyle='#FFD700';ctx.beginPath();ctx.arc(x-30,y+15+Math.sin(time)*10,10,0,Math.PI);ctx.fill();ctx.beginPath();ctx.arc(x+170,y+15+Math.sin(time+1)*10,10,0,Math.PI);ctx.fill();
    if(this.phase>=2){ctx.fillStyle=`rgba(200,200,200,${0.4+Math.sin(time*6)*0.2})`;ctx.beginPath();ctx.arc(x+5,y+40,10+Math.sin(time*4)*3,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+135,y+40,10+Math.sin(time*4+1)*3,0,Math.PI*2);ctx.fill();}
  }

  drawBossInferno(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase===3?'#FFFF00':this.phase===2?'#FF6600':'#FF4500';
    const legOffset = Math.sin(this.animFrame*Math.PI)*8;
    ctx.shadowColor='#FF4500';ctx.shadowBlur=30;
    ctx.fillStyle='#8B0000';ctx.fillRect(x+20,y+85+legOffset,25,40);ctx.fillRect(x+65,y+85-legOffset,25,40);
    for(let i=0;i<3;i++){ctx.fillStyle=`rgba(255,${100+i*50},0,${0.7-i*0.2})`;ctx.beginPath();ctx.moveTo(x+22+i*8,y+85+legOffset);ctx.lineTo(x+32+i*3,y+70+legOffset-Math.sin(time*5+i)*8);ctx.lineTo(x+42-i*8,y+85+legOffset);ctx.fill();}
    ctx.fillStyle='#8B0000';ctx.fillRect(x+5,y+30,100,60);
    ctx.strokeStyle=phaseColor;ctx.lineWidth=3;ctx.beginPath();ctx.moveTo(x+20,y+35);ctx.lineTo(x+40,y+55);ctx.lineTo(x+30,y+80);ctx.stroke();ctx.beginPath();ctx.moveTo(x+80,y+40);ctx.lineTo(x+60,y+60);ctx.lineTo(x+75,y+85);ctx.stroke();
    ctx.fillStyle='#8B0000';ctx.fillRect(x+25,y+5,60,30);
    ctx.fillStyle='#1a1a1a';ctx.beginPath();ctx.moveTo(x+25,y+10);ctx.lineTo(x+10,y-20);ctx.lineTo(x+35,y+5);ctx.fill();ctx.beginPath();ctx.moveTo(x+85,y+10);ctx.lineTo(x+100,y-20);ctx.lineTo(x+75,y+5);ctx.fill();
    ctx.fillStyle=phaseColor;ctx.beginPath();ctx.arc(x+38,y+18,6,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+72,y+18,6,0,Math.PI*2);ctx.fill();
    ctx.fillStyle='#000';ctx.fillRect(x+40,y+28,30,8);
    ctx.fillStyle=phaseColor;for(let i=0;i<4;i++){ctx.beginPath();ctx.moveTo(x+43+i*8,y+36);ctx.lineTo(x+47+i*8,y+42+Math.sin(time*8+i)*3);ctx.lineTo(x+51+i*8,y+36);ctx.fill();}
    ctx.fillStyle='#8B0000';ctx.fillRect(x-15,y+40,25,20);ctx.fillRect(x+100,y+40,25,20);
    ctx.fillStyle=phaseColor;ctx.beginPath();ctx.arc(x-5,y+65,15+Math.sin(time*4)*3,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+115,y+65,15+Math.sin(time*4+1)*3,0,Math.PI*2);ctx.fill();
    for(let i=0;i<5;i++){const flameX=x+15+i*20;const flameHeight=15+Math.sin(time*6+i)*8;ctx.fillStyle=`rgba(255,${50+i*30},0,0.6)`;ctx.beginPath();ctx.moveTo(flameX,y+30);ctx.lineTo(flameX+8,y+30-flameHeight);ctx.lineTo(flameX+16,y+30);ctx.fill();}
    ctx.shadowBlur=0;
  }

  drawBossShadow(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase===3?'#FF3B30':this.phase===2?'#FF9500':'#9945FF';
    const legOffset = Math.sin(this.animFrame*Math.PI)*6;
    ctx.fillStyle=`rgba(25,25,46,${0.3+Math.sin(time)*0.1})`;ctx.beginPath();ctx.arc(x+75,y+60,100+Math.sin(time*2)*10,0,Math.PI*2);ctx.fill();
    ctx.strokeStyle='#1a1a2e';ctx.lineWidth=8;for(let i=0;i<4;i++){const tendrilX=x+20+i*40;ctx.beginPath();ctx.moveTo(tendrilX,y+130);ctx.quadraticCurveTo(tendrilX+Math.sin(time+i)*20,y+100,tendrilX+Math.sin(time*2+i)*15,y+70);ctx.stroke();}
    ctx.fillStyle='#1a1a2e';ctx.fillRect(x+15,y+90+legOffset,40,45);ctx.fillRect(x+95,y+90-legOffset,40,45);
    ctx.strokeStyle=phaseColor;ctx.lineWidth=2;ctx.beginPath();ctx.moveTo(x+25,y+92+legOffset);ctx.lineTo(x+35,y+130+legOffset);ctx.stroke();ctx.beginPath();ctx.moveTo(x+105,y+92-legOffset);ctx.lineTo(x+115,y+130-legOffset);ctx.stroke();
    ctx.fillStyle='#0a0a15';ctx.fillRect(x+5,y+25,140,70);
    ctx.fillStyle=phaseColor;ctx.beginPath();ctx.arc(x+75,y+60,20,0,Math.PI*2);ctx.fill();ctx.fillStyle='#fff';ctx.beginPath();ctx.arc(x+75,y+60,8,0,Math.PI*2);ctx.fill();
    ctx.strokeStyle=phaseColor;ctx.lineWidth=2;for(let i=0;i<6;i++){const angle=(i/6)*Math.PI*2+time;ctx.beginPath();ctx.moveTo(x+75,y+60);ctx.lineTo(x+75+Math.cos(angle)*50,y+60+Math.sin(angle)*30);ctx.stroke();}
    ctx.fillStyle='#0a0a15';ctx.fillRect(x+40,y-10,70,40);
    ctx.fillStyle=phaseColor;for(let i=0;i<5;i++){ctx.beginPath();ctx.moveTo(x+45+i*15,y-10);ctx.lineTo(x+52+i*15,y-30-(i===2?10:0));ctx.lineTo(x+59+i*15,y-10);ctx.fill();}
    ctx.fillStyle=phaseColor;ctx.beginPath();ctx.arc(x+55,y+5,5,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+75,y+10,7,0,Math.PI*2);ctx.fill();ctx.beginPath();ctx.arc(x+95,y+5,5,0,Math.PI*2);ctx.fill();
    if(this.phase>=2){ctx.fillStyle='#FF3B30';ctx.beginPath();ctx.arc(x+75,y-2,4,0,Math.PI*2);ctx.fill();}
    ctx.fillStyle='#0a0a15';const armAngle=Math.sin(time)*0.3;
    ctx.save();ctx.translate(x-5,y+45);ctx.rotate(-0.3+armAngle);ctx.fillRect(-30,-10,40,25);ctx.fillStyle=phaseColor;for(let i=0;i<3;i++){ctx.beginPath();ctx.moveTo(-30,-5+i*10);ctx.lineTo(-45,-8+i*10);ctx.lineTo(-30,i*10);ctx.fill();}ctx.restore();
    ctx.save();ctx.translate(x+155,y+45);ctx.rotate(0.3-armAngle);ctx.fillStyle='#0a0a15';ctx.fillRect(-10,-10,40,25);ctx.fillStyle=phaseColor;for(let i=0;i<3;i++){ctx.beginPath();ctx.moveTo(30,-5+i*10);ctx.lineTo(45,-8+i*10);ctx.lineTo(30,i*10);ctx.fill();}ctx.restore();
    ctx.strokeStyle=phaseColor;ctx.lineWidth=3;ctx.globalAlpha=0.5+Math.sin(time*2)*0.3;ctx.strokeRect(x-5,y-15,160,115);ctx.globalAlpha=1;
  }

  // ============== P-2 Level-themed bosses (Feb 2026) ==============

  // L1 IRON HOUND — quadruped robotic dog with red laser eyes + fireball bark.
  drawBossDog(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.eyeGlow || '#FF3B30';
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const w = this.width, h = this.height;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 2, w * 0.5, 6, 0, 0, Math.PI * 2); ctx.fill();
    // Tail (segmented)
    ctx.fillStyle = this.color;
    for (let i = 0; i < 4; i++) {
      const tx = x - 5 - i * 8, ty = y + h * 0.4 + Math.sin(time * 3 + i) * 4;
      ctx.fillRect(tx, ty, 10 - i, 8 - i);
    }
    // Four legs (front pair + back pair — alternating gait)
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.15, y + h * 0.75 + legOffset, w * 0.1, h * 0.35);
    ctx.fillRect(x + w * 0.7, y + h * 0.75 - legOffset, w * 0.1, h * 0.35);
    ctx.fillRect(x + w * 0.25, y + h * 0.75 - legOffset, w * 0.1, h * 0.35);
    ctx.fillRect(x + w * 0.6, y + h * 0.75 + legOffset, w * 0.1, h * 0.35);
    // Body (rectangular chassis)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.1, y + h * 0.35, w * 0.8, h * 0.45);
    // Armor plates
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.15, y + h * 0.4, w * 0.7, h * 0.08);
    ctx.fillRect(x + w * 0.15, y + h * 0.7, w * 0.7, h * 0.05);
    // Head (blocky, forward)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.7, y + h * 0.1, w * 0.35, h * 0.4);
    // Snout
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.95, y + h * 0.28, w * 0.15, h * 0.15);
    // Twin red laser eyes
    const eyePulse = 0.6 + Math.sin(time * 6) * 0.4;
    ctx.save();
    ctx.shadowColor = phaseColor; ctx.shadowBlur = 15;
    ctx.fillStyle = phaseColor;
    ctx.globalAlpha = eyePulse;
    ctx.beginPath(); ctx.arc(x + w * 0.82, y + h * 0.22, 5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.92, y + h * 0.22, 5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Ears (twitchy)
    ctx.fillStyle = this.secondaryColor;
    const earShift = Math.sin(time * 4) * 2;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.72, y + h * 0.1);
    ctx.lineTo(x + w * 0.78 + earShift, y - h * 0.05);
    ctx.lineTo(x + w * 0.82, y + h * 0.1);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + w * 0.88, y + h * 0.1);
    ctx.lineTo(x + w * 0.92 - earShift, y - h * 0.05);
    ctx.lineTo(x + w * 0.98, y + h * 0.1);
    ctx.fill();
    // Bark / laser telegraph line when about to shoot
    if (this.shootCooldown < 12 && this.shootCooldown > 0) {
      ctx.save();
      ctx.strokeStyle = `rgba(255, 59, 48, ${this.shootCooldown / 12})`;
      ctx.lineWidth = 2;
      const laserY = y + h * 0.22;
      const dir = this.velocityX >= 0 ? 1 : -1;
      const laserStart = dir > 0 ? x + w * 1.1 : x - 5;
      ctx.beginPath(); ctx.moveTo(laserStart, laserY); ctx.lineTo(laserStart + dir * 400, laserY); ctx.stroke();
      ctx.restore();
    }
  }

  // L2 CHAIN LEADER — punk with spinning chain-whip aura + neon graffiti.
  drawBossAlley(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    const legOffset = Math.sin(this.animFrame * Math.PI) * 4;
    const w = this.width, h = this.height;
    // Chain whip trail (spinning wide arc)
    this.chainAngle = (this.chainAngle || 0) + 0.18;
    ctx.save();
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 3;
    for (let seg = 0; seg < 8; seg++) {
      const r = 30 + seg * 8;
      const ang = this.chainAngle + seg * 0.15;
      const sx = x + w * 0.5 + Math.cos(ang) * r;
      const sy = y + h * 0.5 + Math.sin(ang) * r * 0.4;
      ctx.fillStyle = seg === 7 ? phaseColor : '#666';
      ctx.beginPath(); ctx.arc(sx, sy, 4, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 2, w * 0.5, 5, 0, 0, Math.PI * 2); ctx.fill();
    // Legs (baggy pants)
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(x + w * 0.2, y + h * 0.65 + legOffset, w * 0.25, h * 0.4);
    ctx.fillRect(x + w * 0.55, y + h * 0.65 - legOffset, w * 0.25, h * 0.4);
    // Body — magenta leather jacket
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.15, y + h * 0.3, w * 0.7, h * 0.4);
    // Spike accents on shoulders
    ctx.fillStyle = phaseColor;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * (0.18 + i * 0.08), y + h * 0.3);
      ctx.lineTo(x + w * (0.22 + i * 0.08), y + h * 0.18);
      ctx.lineTo(x + w * (0.26 + i * 0.08), y + h * 0.3);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x + w * (0.58 + i * 0.08), y + h * 0.3);
      ctx.lineTo(x + w * (0.62 + i * 0.08), y + h * 0.18);
      ctx.lineTo(x + w * (0.66 + i * 0.08), y + h * 0.3);
      ctx.fill();
    }
    // Head — mohawk punk
    ctx.fillStyle = '#d4a5a5';
    ctx.fillRect(x + w * 0.3, y + h * 0.08, w * 0.4, h * 0.25);
    // Mohawk (cyan spikes)
    ctx.fillStyle = this.secondaryColor;
    for (let i = 0; i < 5; i++) {
      const mx = x + w * (0.35 + i * 0.06);
      ctx.beginPath();
      ctx.moveTo(mx, y + h * 0.08);
      ctx.lineTo(mx + 4, y - h * 0.05);
      ctx.lineTo(mx + 8, y + h * 0.08);
      ctx.fill();
    }
    // Glowing eyes
    ctx.fillStyle = phaseColor;
    ctx.fillRect(x + w * 0.36, y + h * 0.16, w * 0.08, h * 0.04);
    ctx.fillRect(x + w * 0.56, y + h * 0.16, w * 0.08, h * 0.04);
    // Neon graffiti aura
    ctx.save();
    ctx.globalAlpha = 0.4 + Math.sin(time * 2) * 0.2;
    ctx.strokeStyle = this.secondaryColor;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x - 4, y - 8, w + 8, h + 12);
    ctx.restore();
  }

  // L3 DUNE DEVOURER — segmented sand worm that burrows and erupts.
  drawBossWorm(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.color;
    const w = this.width, h = this.height;
    // Underground state — draw only sand mound above worm's tracking position
    if (this.wormBurrowPhase === 2) {
      ctx.save();
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.ellipse(x + w * 0.5, y + h + 6, w * 0.4, 12, 0, 0, Math.PI * 2);
      ctx.fill();
      // Debris puffs
      ctx.fillStyle = 'rgba(150, 120, 80, 0.4)';
      for (let i = 0; i < 4; i++) {
        const dx = x + w * 0.3 + i * 15 + Math.sin(time * 3 + i) * 8;
        ctx.beginPath(); ctx.arc(dx, y + h - 4, 4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      return;
    }
    // Above-ground — draw segmented body rising from sand
    const eruptOffset = this.wormBurrowPhase === 3 ? (1 - this.wormPhaseTimer / 30) * h * 0.4 : 0;
    // Ground-line dust cloud around base
    ctx.fillStyle = 'rgba(150, 120, 80, 0.5)';
    ctx.beginPath();
    ctx.ellipse(x + w * 0.5, y + h - 4 + eruptOffset, w * 0.55, 8, 0, 0, Math.PI * 2);
    ctx.fill();
    // 6 segments, alternating shades, tapering
    for (let seg = 0; seg < 6; seg++) {
      const segR = (6 - seg) * 8;
      const segY = y + h - eruptOffset + seg * 12 - seg * 6;
      const segX = x + w * 0.5 + Math.sin(time * 2 + seg * 0.6) * 6;
      ctx.fillStyle = seg % 2 === 0 ? this.color : this.secondaryColor;
      ctx.beginPath(); ctx.arc(segX, segY, segR, 0, Math.PI * 2); ctx.fill();
      // Segment ridge
      ctx.strokeStyle = this.secondaryColor;
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(segX, segY, segR, Math.PI * 0.9, Math.PI * 1.1); ctx.stroke();
    }
    // Head at top of column
    const headY = y - eruptOffset + h * 0.1;
    const headX = x + w * 0.5 + Math.sin(time * 2) * 8;
    ctx.fillStyle = this.color;
    ctx.beginPath(); ctx.arc(headX, headY, 32, 0, Math.PI * 2); ctx.fill();
    // Toothy circular maw
    ctx.fillStyle = '#1a0a05';
    ctx.beginPath(); ctx.arc(headX, headY, 20, 0, Math.PI * 2); ctx.fill();
    // Teeth around maw (6 triangular)
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + time;
      const t1x = headX + Math.cos(a) * 20, t1y = headY + Math.sin(a) * 20;
      const t2x = headX + Math.cos(a + 0.15) * 14, t2y = headY + Math.sin(a + 0.15) * 14;
      const t3x = headX + Math.cos(a - 0.15) * 14, t3y = headY + Math.sin(a - 0.15) * 14;
      ctx.beginPath(); ctx.moveTo(t1x, t1y); ctx.lineTo(t2x, t2y); ctx.lineTo(t3x, t3y); ctx.fill();
    }
    // Central maw glow when about to bite
    if (this.wormBurrowPhase === 3 || this.shootCooldown < 20) {
      ctx.save();
      ctx.shadowColor = phaseColor;
      ctx.shadowBlur = 20;
      ctx.fillStyle = phaseColor;
      ctx.beginPath(); ctx.arc(headX, headY, 8, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  // L4 DESERT PROWLER — armored truck with mounted turret + salvo bursts.
  drawBossHighway(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    const w = this.width, h = this.height;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.4)'; ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 3, w * 0.5, 6, 0, 0, Math.PI * 2); ctx.fill();
    // Wheels (4, spinning marks)
    ctx.fillStyle = '#111';
    for (const cx of [w * 0.15, w * 0.4, w * 0.6, w * 0.85]) {
      ctx.beginPath(); ctx.arc(x + cx, y + h * 0.85, h * 0.18, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = phaseColor; ctx.lineWidth = 2;
      ctx.save(); ctx.translate(x + cx, y + h * 0.85); ctx.rotate(time * 8);
      ctx.beginPath(); ctx.moveTo(-h * 0.15, 0); ctx.lineTo(h * 0.15, 0); ctx.stroke();
      ctx.restore();
    }
    // Chassis
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.05, y + h * 0.45, w * 0.9, h * 0.4);
    // Cargo bed rear
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.4, y + h * 0.25, w * 0.4, h * 0.25);
    // Cab (front)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.05, y + h * 0.25, w * 0.35, h * 0.3);
    // Windshield
    ctx.fillStyle = 'rgba(120, 200, 255, 0.5)';
    ctx.fillRect(x + w * 0.08, y + h * 0.3, w * 0.28, h * 0.2);
    // Mounted turret (on cargo bed)
    ctx.fillStyle = phaseColor;
    ctx.fillRect(x + w * 0.55, y + h * 0.05, w * 0.15, h * 0.25);
    // Turret barrel
    ctx.fillStyle = '#333';
    const dir = this.velocityX >= 0 ? 1 : -1;
    const barrelX = dir > 0 ? x + w * 0.7 : x + w * 0.55;
    ctx.fillRect(barrelX, y + h * 0.1, w * 0.25 * dir, h * 0.06);
    // Muzzle flash if firing this frame
    if (this.shootCooldown > this.shootRate - 3) {
      ctx.save();
      ctx.fillStyle = 'rgba(255, 220, 50, 0.9)';
      ctx.beginPath(); ctx.arc(barrelX + w * 0.25 * dir, y + h * 0.13, 6, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
    // Armor plate rivets
    ctx.fillStyle = this.secondaryColor;
    for (let i = 0; i < 5; i++) ctx.fillRect(x + w * (0.1 + i * 0.15), y + h * 0.5, 3, 3);
  }

  // L5 CONCRETE COLOSSUS — crane robot with a swinging wrecking ball.
  drawBossQuarry(ctx, x, y) {
    const time = Date.now() / 1000;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : '#5AC8FA';
    const w = this.width, h = this.height;
    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.4)'; ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 3, w * 0.55, 7, 0, 0, Math.PI * 2); ctx.fill();
    // Tracked base (bulldozer-style)
    ctx.fillStyle = '#111';
    ctx.fillRect(x + w * 0.05, y + h * 0.8, w * 0.9, h * 0.18);
    ctx.fillStyle = this.secondaryColor;
    for (let i = 0; i < 8; i++) ctx.fillRect(x + w * (0.08 + i * 0.11), y + h * 0.82, w * 0.06, h * 0.14);
    // Cabin (main body)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.15, y + h * 0.35, w * 0.7, h * 0.45);
    // Cabin window
    ctx.fillStyle = 'rgba(120, 200, 255, 0.6)';
    ctx.fillRect(x + w * 0.2, y + h * 0.4, w * 0.25, h * 0.2);
    // Yellow-black safety stripes
    ctx.fillStyle = '#FFD700';
    for (let i = 0; i < 4; i++) ctx.fillRect(x + w * 0.5, y + h * (0.42 + i * 0.08), w * 0.32, h * 0.03);
    // Crane arm (extends up and diagonally)
    ctx.strokeStyle = this.secondaryColor;
    ctx.lineWidth = 8;
    const armBaseX = x + w * 0.85, armBaseY = y + h * 0.35;
    const armTipX = armBaseX + 40, armTipY = armBaseY - 60;
    ctx.beginPath(); ctx.moveTo(armBaseX, armBaseY); ctx.lineTo(armTipX, armTipY); ctx.stroke();
    // Cable + wrecking ball (swings)
    this.ballAngle = (this.ballAngle || 0) + 0.05;
    const swing = Math.sin(this.ballAngle) * 60;
    const ballX = armTipX + swing;
    const ballY = armTipY + Math.abs(Math.sin(this.ballAngle)) * 40 + 50;
    ctx.strokeStyle = '#888'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(armTipX, armTipY); ctx.lineTo(ballX, ballY); ctx.stroke();
    // Ball itself
    ctx.fillStyle = '#333';
    ctx.beginPath(); ctx.arc(ballX, ballY, 16, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(ballX - 4, ballY - 4, 4, 0, Math.PI * 2); ctx.fill();
    // Emergency beacon light on top
    ctx.save();
    ctx.shadowColor = phaseColor; ctx.shadowBlur = 15;
    ctx.fillStyle = `rgba(${this.phase >= 2 ? '255,59,48' : '90,200,250'}, ${0.5 + Math.sin(time * 4) * 0.4})`;
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.32, 6, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L6 FACTORY FOREMAN — hard hat, hydraulic press arm, mustachioed brute.
  drawBossFactory(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.color;
    // Legs (work boots)
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(x + w * 0.2, y + h * 0.7, w * 0.2, h * 0.32);
    ctx.fillRect(x + w * 0.55, y + h * 0.7, w * 0.2, h * 0.32);
    // Body (orange coveralls)
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.15, y + h * 0.35, w * 0.7, h * 0.4);
    // Belt with tools
    ctx.fillStyle = this.secondaryColor; ctx.fillRect(x + w * 0.15, y + h * 0.6, w * 0.7, h * 0.06);
    // Head (skin tone)
    ctx.fillStyle = '#d4a86a'; ctx.fillRect(x + w * 0.3, y + h * 0.1, w * 0.4, h * 0.23);
    // Hard hat (yellow)
    ctx.fillStyle = '#FFD700'; ctx.fillRect(x + w * 0.28, y + h * 0.05, w * 0.44, h * 0.12);
    ctx.fillRect(x + w * 0.25, y + h * 0.13, w * 0.5, h * 0.04);
    // Mustache
    ctx.fillStyle = '#2a1a0a'; ctx.fillRect(x + w * 0.35, y + h * 0.22, w * 0.3, h * 0.04);
    // Hydraulic press arm (raised, telegraph slam)
    this.pressAngle = (this.pressAngle || 0) + 0.05;
    const armY = y + h * 0.4 + Math.sin(this.pressAngle) * 15;
    ctx.fillStyle = this.secondaryColor; ctx.fillRect(x + w * 0.85, armY, w * 0.3, h * 0.35);
    ctx.fillStyle = phaseColor; ctx.fillRect(x + w * 1.1, armY + h * 0.3, w * 0.15, h * 0.15);
    // Smokestack puff behind
    ctx.fillStyle = 'rgba(80,80,80,0.5)';
    for (let i = 0; i < 3; i++) ctx.beginPath(), ctx.arc(x - 15 - i * 12, y - 10 + Math.sin(t + i) * 5, 8 + i * 2, 0, Math.PI * 2), ctx.fill();
  }

  // L7 BRIDGE TROLL — fat troll with wooden club, moss beard, bulging belly.
  drawBossBridge(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // Legs (chunky)
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.15, y + h * 0.7, w * 0.25, h * 0.32);
    ctx.fillRect(x + w * 0.6, y + h * 0.7, w * 0.25, h * 0.32);
    // Round belly
    ctx.fillStyle = this.color; ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.55, w * 0.42, 0, Math.PI * 2); ctx.fill();
    // Head
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.22, w * 0.3, 0, Math.PI * 2); ctx.fill();
    // Moss beard (dangles)
    ctx.strokeStyle = '#2E3A18'; ctx.lineWidth = 3;
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * (0.3 + i * 0.08), y + h * 0.3);
      ctx.lineTo(x + w * (0.3 + i * 0.08) + Math.sin(t + i) * 2, y + h * 0.5);
      ctx.stroke();
    }
    // Angry yellow eyes
    ctx.fillStyle = '#FFD700';
    ctx.fillRect(x + w * 0.38, y + h * 0.18, w * 0.08, h * 0.05);
    ctx.fillRect(x + w * 0.54, y + h * 0.18, w * 0.08, h * 0.05);
    // Wooden club (angled, right hand)
    ctx.save(); ctx.translate(x + w * 0.9, y + h * 0.4); ctx.rotate(Math.sin(t * 2) * 0.3);
    ctx.fillStyle = '#8B4513'; ctx.fillRect(-8, -h * 0.4, 16, h * 0.5);
    ctx.fillStyle = phaseColor; ctx.beginPath(); ctx.arc(0, -h * 0.4, 20, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L8 DOCK KRAKEN — huge octopus emerging from below, waving tentacles.
  drawBossKraken(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // Water splash below
    ctx.fillStyle = 'rgba(100,150,180,0.4)';
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 5, w * 0.55, 12, 0, 0, Math.PI * 2); ctx.fill();
    // 6 tentacles waving in a fan
    ctx.strokeStyle = this.color; ctx.lineWidth = 12;
    for (let i = 0; i < 6; i++) {
      const baseAng = (i / 5) * Math.PI - Math.PI / 2;
      const wave = Math.sin(t * 2 + i) * 25;
      const startX = x + w * 0.5 + Math.cos(baseAng) * w * 0.25;
      const startY = y + h * 0.6;
      const midX = x + w * 0.5 + Math.cos(baseAng) * w * 0.6 + wave;
      const midY = y + h * 0.4;
      const endX = x + w * 0.5 + Math.cos(baseAng) * w * 0.85;
      const endY = y + h * 0.15 - i * 3;
      ctx.beginPath(); ctx.moveTo(startX, startY); ctx.quadraticCurveTo(midX, midY, endX, endY); ctx.stroke();
    }
    // Central head
    ctx.fillStyle = this.color; ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.55, w * 0.28, 0, Math.PI * 2); ctx.fill();
    // Glowing eye
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 20;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.5, 14, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#000'; ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.5, 5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L9 VOLCANIC EFREET — flame djinn with lava tail, obsidian crown.
  drawBossEfreet(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FFD700' : this.phase === 2 ? '#FF9500' : '#FF4500';
    // Lava trail below (no legs)
    ctx.fillStyle = 'rgba(255,80,0,0.6)';
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h * 0.95, w * 0.4, h * 0.15, 0, 0, Math.PI * 2); ctx.fill();
    // Flame body (tapered wisps)
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.95);
    for (let i = 0; i <= 20; i++) {
      const py = y + h * (0.95 - i * 0.045);
      const flare = Math.sin(t * 3 + i * 0.5) * 6;
      const px = x + w * (0.5 + Math.sin(i * 0.3) * 0.15) + flare;
      ctx.lineTo(px, py);
    }
    for (let i = 20; i >= 0; i--) {
      const py = y + h * (0.95 - i * 0.045);
      const flare = Math.sin(t * 3 + i * 0.5 + Math.PI) * 6;
      const px = x + w * (0.5 - Math.sin(i * 0.3) * 0.15) + flare;
      ctx.lineTo(px, py);
    }
    ctx.closePath(); ctx.fill();
    // Inner flame
    ctx.fillStyle = phaseColor; ctx.globalAlpha = 0.7;
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h * 0.4, w * 0.2, h * 0.35, 0, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    // Obsidian crown
    ctx.fillStyle = '#1a0a0a';
    for (let i = 0; i < 5; i++) {
      const cx = x + w * (0.3 + i * 0.1);
      ctx.beginPath(); ctx.moveTo(cx, y + h * 0.15); ctx.lineTo(cx + 5, y - h * 0.02); ctx.lineTo(cx + 10, y + h * 0.15); ctx.fill();
    }
    // Glowing eyes
    ctx.save(); ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 12;
    ctx.fillStyle = '#FFD700';
    ctx.beginPath(); ctx.arc(x + w * 0.42, y + h * 0.28, 4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.58, y + h * 0.28, 4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L10 NEON RUNNER PRIME — cyber-jock with holo-visor + dual pistols + LED trail.
  drawBossRunner(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.color;
    // LED trail (motion streaks)
    ctx.save(); ctx.globalAlpha = 0.35;
    for (let i = 1; i <= 3; i++) {
      ctx.fillStyle = phaseColor;
      ctx.fillRect(x - i * 10, y + h * 0.2, w * 0.7, h * 0.6);
    }
    ctx.restore();
    // Legs
    ctx.fillStyle = '#1a1a2e';
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    ctx.fillRect(x + w * 0.25, y + h * 0.65 + legOffset, w * 0.2, h * 0.35);
    ctx.fillRect(x + w * 0.55, y + h * 0.65 - legOffset, w * 0.2, h * 0.35);
    // Body (racing suit)
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.2, y + h * 0.3, w * 0.6, h * 0.4);
    // LED stripes
    ctx.fillStyle = phaseColor;
    ctx.fillRect(x + w * 0.22, y + h * 0.4, w * 0.06, h * 0.25);
    ctx.fillRect(x + w * 0.72, y + h * 0.4, w * 0.06, h * 0.25);
    // Head + holo visor
    ctx.fillStyle = '#d4a86a'; ctx.fillRect(x + w * 0.3, y + h * 0.08, w * 0.4, h * 0.2);
    ctx.fillStyle = this.secondaryColor;
    ctx.globalAlpha = 0.7 + Math.sin(t * 4) * 0.3;
    ctx.fillRect(x + w * 0.28, y + h * 0.13, w * 0.44, h * 0.08);
    ctx.globalAlpha = 1;
    // Dual pistols (extended)
    ctx.fillStyle = '#222';
    ctx.fillRect(x + w * 0.05, y + h * 0.4, w * 0.2, h * 0.06);
    ctx.fillRect(x + w * 0.75, y + h * 0.4, w * 0.2, h * 0.06);
  }

  // L11 ARCTIC WRAITH — floating ice ghost with translucent robe + crystal shards.
  drawBossWraith(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.color;
    this.floatOffset = Math.sin(t * 2) * 8;
    const offsetY = this.floatOffset;
    // Translucent robe (tapered)
    ctx.save(); ctx.globalAlpha = 0.6;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + offsetY);
    ctx.lineTo(x + w * 0.85, y + h + offsetY);
    ctx.lineTo(x + w * 0.15, y + h + offsetY);
    ctx.closePath(); ctx.fill();
    ctx.restore();
    // Head (icy skull)
    ctx.fillStyle = '#e0f5ff';
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.15 + offsetY, w * 0.2, 0, Math.PI * 2); ctx.fill();
    // Hollow eyes (glowing)
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 15;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.42, y + h * 0.15 + offsetY, 5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.58, y + h * 0.15 + offsetY, 5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Crystal shards orbiting
    ctx.fillStyle = this.secondaryColor;
    for (let i = 0; i < 4; i++) {
      const ang = t + i * Math.PI / 2;
      const cx = x + w * 0.5 + Math.cos(ang) * 40;
      const cy = y + h * 0.5 + offsetY + Math.sin(ang) * 30;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 8); ctx.lineTo(cx + 5, cy); ctx.lineTo(cx, cy + 8); ctx.lineTo(cx - 5, cy);
      ctx.closePath(); ctx.fill();
    }
  }

  // L12 SNOW YETI — shaggy beast with red eyes and wide arms.
  drawBossYeti(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : '#FF3B30';
    const legOffset = Math.sin(this.animFrame * Math.PI) * 4;
    // Legs
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.2, y + h * 0.75 + legOffset, w * 0.25, h * 0.3);
    ctx.fillRect(x + w * 0.55, y + h * 0.75 - legOffset, w * 0.25, h * 0.3);
    // Wide arms
    ctx.fillRect(x - 5, y + h * 0.35, w * 0.2, h * 0.4);
    ctx.fillRect(x + w * 0.85, y + h * 0.35, w * 0.2, h * 0.4);
    // Body (round shaggy)
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.55, w * 0.4, 0, Math.PI * 2); ctx.fill();
    // Head
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.22, w * 0.25, 0, Math.PI * 2); ctx.fill();
    // Shaggy fur strokes
    ctx.strokeStyle = this.secondaryColor; ctx.lineWidth = 2;
    for (let i = 0; i < 12; i++) {
      const ang = (i / 12) * Math.PI * 2 + t * 0.5;
      const rx = x + w * 0.5 + Math.cos(ang) * w * 0.4;
      const ry = y + h * 0.55 + Math.sin(ang) * w * 0.4;
      ctx.beginPath(); ctx.moveTo(rx, ry); ctx.lineTo(rx + Math.cos(ang) * 8, ry + Math.sin(ang) * 8); ctx.stroke();
    }
    // Red glowing eyes
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 14;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.42, y + h * 0.22, 5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.58, y + h * 0.22, 5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Fangs
    ctx.fillStyle = '#fff';
    ctx.beginPath(); ctx.moveTo(x + w * 0.45, y + h * 0.3); ctx.lineTo(x + w * 0.47, y + h * 0.38); ctx.lineTo(x + w * 0.5, y + h * 0.3); ctx.fill();
    ctx.beginPath(); ctx.moveTo(x + w * 0.5, y + h * 0.3); ctx.lineTo(x + w * 0.53, y + h * 0.38); ctx.lineTo(x + w * 0.55, y + h * 0.3); ctx.fill();
  }

  // L13 JUNGLE VINE KING — humanoid vine golem with leafy shoulders + thorny fists.
  drawBossVine(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    this.vineWave = t * 2;
    // Legs (vine bundles)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.25, y + h * 0.65, w * 0.15, h * 0.4);
    ctx.fillRect(x + w * 0.6, y + h * 0.65, w * 0.15, h * 0.4);
    // Body (thick trunk)
    ctx.fillRect(x + w * 0.2, y + h * 0.3, w * 0.6, h * 0.4);
    // Leaf shoulders
    ctx.fillStyle = this.secondaryColor;
    for (let side of [-1, 1]) {
      const sx = x + (side < 0 ? w * 0.15 : w * 0.75);
      for (let i = 0; i < 3; i++) {
        const ang = (i / 3) * Math.PI + Math.sin(this.vineWave + i) * 0.2;
        ctx.beginPath();
        ctx.ellipse(sx, y + h * 0.35, 18, 8, ang, 0, Math.PI * 2); ctx.fill();
      }
    }
    // Head (vine crown)
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.3, y + h * 0.08, w * 0.4, h * 0.22);
    // Crown of thorns
    ctx.strokeStyle = this.secondaryColor; ctx.lineWidth = 3;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * (0.3 + i * 0.1), y + h * 0.08);
      ctx.lineTo(x + w * (0.32 + i * 0.1), y - h * 0.02);
      ctx.stroke();
    }
    // Glowing green eyes
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 12;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.4, y + h * 0.18, 4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.6, y + h * 0.18, 4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Thorny fists (extended vines)
    ctx.strokeStyle = this.color; ctx.lineWidth = 8;
    for (let side of [-1, 1]) {
      const sx = x + w * 0.5 + side * w * 0.35;
      const ex = sx + side * (30 + Math.sin(this.vineWave) * 10);
      ctx.beginPath(); ctx.moveTo(sx, y + h * 0.45); ctx.lineTo(ex, y + h * 0.55); ctx.stroke();
    }
  }

  // L14 SWAMP TOXIN — gasmask chem-lord with tank backpack + acid drips.
  drawBossToxin(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // Acid drip pool
    ctx.fillStyle = 'rgba(154,205,50,0.4)';
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h + 4, w * 0.4, 6, 0, 0, Math.PI * 2); ctx.fill();
    // Legs (rubber boots)
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(x + w * 0.25, y + h * 0.7, w * 0.2, h * 0.32);
    ctx.fillRect(x + w * 0.55, y + h * 0.7, w * 0.2, h * 0.32);
    // Body (hazmat suit)
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.2, y + h * 0.3, w * 0.6, h * 0.42);
    // Tank backpack
    ctx.fillStyle = '#333';
    ctx.fillRect(x + w * 0.05, y + h * 0.28, w * 0.15, h * 0.35);
    ctx.fillRect(x + w * 0.8, y + h * 0.28, w * 0.15, h * 0.35);
    // Glowing green tank contents
    ctx.fillStyle = phaseColor; ctx.globalAlpha = 0.7 + Math.sin(t * 3) * 0.3;
    ctx.fillRect(x + w * 0.07, y + h * 0.32, w * 0.11, h * 0.28);
    ctx.fillRect(x + w * 0.82, y + h * 0.32, w * 0.11, h * 0.28);
    ctx.globalAlpha = 1;
    // Head (gas mask)
    ctx.fillStyle = '#333'; ctx.fillRect(x + w * 0.3, y + h * 0.05, w * 0.4, h * 0.28);
    // Twin filter canisters
    ctx.fillStyle = '#555';
    ctx.beginPath(); ctx.arc(x + w * 0.36, y + h * 0.22, 6, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.64, y + h * 0.22, 6, 0, Math.PI * 2); ctx.fill();
    // Glowing lenses
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 15;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.42, y + h * 0.15, 5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + w * 0.58, y + h * 0.15, 5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Drips
    this.dripTimer = (this.dripTimer || 0) + 1;
    ctx.fillStyle = phaseColor;
    for (let i = 0; i < 3; i++) {
      const dropY = ((this.dripTimer + i * 20) % 60) / 60 * h * 0.4 + h * 0.5;
      ctx.beginPath(); ctx.arc(x + w * (0.35 + i * 0.15), y + dropY, 3, 0, Math.PI * 2); ctx.fill();
    }
  }

  // L15 STATION SENTINEL — quadruped guardian with sweeping searchlight.
  drawBossSentinel(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // 4 legs
    ctx.fillStyle = this.color;
    for (const cx of [0.1, 0.35, 0.6, 0.85]) ctx.fillRect(x + w * cx, y + h * 0.65, w * 0.08, h * 0.4);
    // Body (long chassis)
    ctx.fillRect(x + w * 0.05, y + h * 0.35, w * 0.9, h * 0.35);
    // Rivet lines
    ctx.fillStyle = '#333';
    for (let i = 0; i < 8; i++) ctx.fillRect(x + w * (0.1 + i * 0.1), y + h * 0.42, 3, 3);
    // Head turret
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.4, y + h * 0.15, w * 0.2, h * 0.22);
    // Searchlight cone
    this.searchlightAngle = (this.searchlightAngle || 0) + 0.03;
    const swAng = Math.sin(this.searchlightAngle) * 0.6;
    ctx.save(); ctx.globalAlpha = 0.3;
    ctx.fillStyle = phaseColor;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.25);
    ctx.lineTo(x + w * 0.5 + Math.cos(swAng - 0.3) * 200, y + h * 0.25 + Math.sin(swAng - 0.3) * 100);
    ctx.lineTo(x + w * 0.5 + Math.cos(swAng + 0.3) * 200, y + h * 0.25 + Math.sin(swAng + 0.3) * 100);
    ctx.closePath(); ctx.fill();
    ctx.restore();
    // Turret eye
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 15;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.25, 6, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L16 COSMIC WARLORD — alien overlord with star cape + energy staff.
  drawBossCosmic(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // Star cape (starfield trail)
    ctx.save(); ctx.globalAlpha = 0.6;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.3);
    ctx.lineTo(x + w * 0.95, y + h * 0.9);
    ctx.lineTo(x + w * 0.05, y + h * 0.9);
    ctx.closePath(); ctx.fill();
    // Stars in cape
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 10; i++) {
      const sx = x + w * (0.15 + (i * 0.08) % 0.7);
      const sy = y + h * (0.5 + (i * 0.06) % 0.3);
      const sr = 1 + Math.sin(t * 3 + i) * 1;
      ctx.beginPath(); ctx.arc(sx, sy, sr, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
    // Body (armor suit)
    ctx.fillStyle = this.secondaryColor; ctx.fillRect(x + w * 0.3, y + h * 0.35, w * 0.4, h * 0.4);
    // Head (elongated alien)
    ctx.fillStyle = '#9370DB'; ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h * 0.15, w * 0.15, h * 0.15, 0, 0, Math.PI * 2); ctx.fill();
    // Big black eyes
    ctx.fillStyle = '#000';
    ctx.beginPath(); ctx.ellipse(x + w * 0.42, y + h * 0.15, 5, 8, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x + w * 0.58, y + h * 0.15, 5, 8, 0, 0, Math.PI * 2); ctx.fill();
    // Energy staff (held right)
    ctx.strokeStyle = '#666'; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(x + w * 0.85, y + h * 0.35); ctx.lineTo(x + w * 0.9, y + h * 0.95); ctx.stroke();
    // Staff orb (pulsing)
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 20;
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.arc(x + w * 0.85, y + h * 0.32, 8 + Math.sin(t * 4) * 2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // L17 CASTLE WRAITH — armored knight with two-handed sword + glowing visor.
  drawBossCastle(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    // Legs (armored)
    ctx.fillStyle = this.color;
    ctx.fillRect(x + w * 0.25, y + h * 0.65, w * 0.2, h * 0.4);
    ctx.fillRect(x + w * 0.55, y + h * 0.65, w * 0.2, h * 0.4);
    // Body (chest plate)
    ctx.fillRect(x + w * 0.15, y + h * 0.3, w * 0.7, h * 0.4);
    // Gold trim
    ctx.fillStyle = this.secondaryColor;
    ctx.fillRect(x + w * 0.2, y + h * 0.32, w * 0.6, h * 0.03);
    ctx.fillRect(x + w * 0.2, y + h * 0.65, w * 0.6, h * 0.03);
    // Helmet
    ctx.fillStyle = this.color; ctx.fillRect(x + w * 0.3, y + h * 0.05, w * 0.4, h * 0.25);
    // Visor slit (glowing)
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 12;
    ctx.fillStyle = phaseColor;
    ctx.fillRect(x + w * 0.32, y + h * 0.14, w * 0.36, h * 0.04);
    ctx.restore();
    // Helmet plume
    ctx.fillStyle = '#8B0000';
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * (0.4 + i * 0.05), y + h * 0.05);
      ctx.lineTo(x + w * (0.42 + i * 0.05), y - h * 0.05);
      ctx.lineTo(x + w * (0.44 + i * 0.05), y + h * 0.05);
      ctx.fill();
    }
    // Two-handed sword
    this.swordAngle = Math.sin(t * 1.5) * 0.4;
    ctx.save(); ctx.translate(x + w * 0.5, y + h * 0.4); ctx.rotate(this.swordAngle);
    ctx.fillStyle = '#c0c0c0'; ctx.fillRect(-3, -h * 0.6, 6, h * 0.6);
    ctx.fillStyle = this.secondaryColor; ctx.fillRect(-12, -h * 0.02, 24, 4);
    ctx.restore();
  }

  // L18 RUIN PHOENIX — giant flaming bird with spread wings.
  drawBossPhoenix(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FFD700' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    this.wingBeat = Math.sin(t * 3);
    // Spread wings
    ctx.fillStyle = this.color;
    ctx.save(); ctx.translate(x + w * 0.5, y + h * 0.5);
    // Left wing
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(-w * 0.4, -h * 0.2 + this.wingBeat * 10, -w * 0.5, h * 0.1);
    ctx.quadraticCurveTo(-w * 0.3, h * 0.15, 0, 0);
    ctx.fill();
    // Right wing
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(w * 0.4, -h * 0.2 + this.wingBeat * 10, w * 0.5, h * 0.1);
    ctx.quadraticCurveTo(w * 0.3, h * 0.15, 0, 0);
    ctx.fill();
    ctx.restore();
    // Body (flame trail effect)
    ctx.fillStyle = phaseColor;
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h * 0.55, w * 0.12, h * 0.3, 0, 0, Math.PI * 2); ctx.fill();
    // Head + beak
    ctx.fillStyle = this.color;
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.3, w * 0.1, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.moveTo(x + w * 0.6, y + h * 0.3);
    ctx.lineTo(x + w * 0.72, y + h * 0.32);
    ctx.lineTo(x + w * 0.6, y + h * 0.34);
    ctx.fill();
    // Fiery crest
    ctx.fillStyle = '#FF9500';
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * (0.44 + i * 0.04), y + h * 0.22);
      ctx.lineTo(x + w * (0.46 + i * 0.04), y + h * 0.1);
      ctx.lineTo(x + w * (0.48 + i * 0.04), y + h * 0.22);
      ctx.fill();
    }
    // Glowing eye
    ctx.save(); ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 12;
    ctx.fillStyle = '#FFD700';
    ctx.beginPath(); ctx.arc(x + w * 0.55, y + h * 0.28, 3, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Tail feathers
    ctx.strokeStyle = phaseColor; ctx.lineWidth = 4;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(x + w * 0.5, y + h * 0.8);
      ctx.lineTo(x + w * (0.4 + i * 0.1), y + h + 15);
      ctx.stroke();
    }
  }

  // L19 CRYSTAL GUARDIAN — crystalline golem with refracting gem heart.
  drawBossCrystal(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FF3B30' : this.phase === 2 ? '#FF9500' : this.secondaryColor;
    this.refractPulse = t * 3;
    // Body (crystal shards)
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.05);
    ctx.lineTo(x + w * 0.85, y + h * 0.35);
    ctx.lineTo(x + w * 0.9, y + h * 0.75);
    ctx.lineTo(x + w * 0.5, y + h * 0.95);
    ctx.lineTo(x + w * 0.1, y + h * 0.75);
    ctx.lineTo(x + w * 0.15, y + h * 0.35);
    ctx.closePath(); ctx.fill();
    // Refractive facets
    ctx.strokeStyle = this.secondaryColor; ctx.lineWidth = 2;
    ctx.globalAlpha = 0.7 + Math.sin(this.refractPulse) * 0.3;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.05); ctx.lineTo(x + w * 0.5, y + h * 0.95);
    ctx.moveTo(x + w * 0.15, y + h * 0.35); ctx.lineTo(x + w * 0.9, y + h * 0.75);
    ctx.moveTo(x + w * 0.85, y + h * 0.35); ctx.lineTo(x + w * 0.1, y + h * 0.75);
    ctx.stroke();
    ctx.globalAlpha = 1;
    // Central gem heart
    ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 25;
    ctx.fillStyle = phaseColor;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + h * 0.4);
    ctx.lineTo(x + w * 0.6, y + h * 0.55);
    ctx.lineTo(x + w * 0.5, y + h * 0.7);
    ctx.lineTo(x + w * 0.4, y + h * 0.55);
    ctx.closePath(); ctx.fill();
    ctx.restore();
    // Orbiting crystal fragments
    ctx.fillStyle = this.secondaryColor;
    for (let i = 0; i < 5; i++) {
      const ang = t + i * (Math.PI * 2 / 5);
      const cx = x + w * 0.5 + Math.cos(ang) * (w * 0.5 + 15);
      const cy = y + h * 0.55 + Math.sin(ang) * (h * 0.4);
      ctx.beginPath();
      ctx.moveTo(cx, cy - 6); ctx.lineTo(cx + 4, cy); ctx.lineTo(cx, cy + 6); ctx.lineTo(cx - 4, cy);
      ctx.closePath(); ctx.fill();
    }
  }

  // L20 SHADOW OMEGA — final boss, multi-eyed void being with tentacles.
  drawBossOmega(ctx, x, y) {
    const t = Date.now() / 1000, w = this.width, h = this.height;
    const phaseColor = this.phase === 3 ? '#FFD700' : this.phase === 2 ? '#FF3B30' : this.secondaryColor;
    // Dark aura (large radial fade)
    ctx.save(); ctx.globalAlpha = 0.5;
    const grad = ctx.createRadialGradient(x + w * 0.5, y + h * 0.5, 0, x + w * 0.5, y + h * 0.5, w);
    grad.addColorStop(0, 'rgba(26,0,0,0.9)');
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.5, w, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // Central mass (irregular blob)
    ctx.fillStyle = this.color;
    ctx.beginPath();
    for (let i = 0; i <= 12; i++) {
      const ang = (i / 12) * Math.PI * 2;
      const r = w * (0.35 + Math.sin(t * 2 + i) * 0.05);
      const px = x + w * 0.5 + Math.cos(ang) * r;
      const py = y + h * 0.5 + Math.sin(ang) * r * 0.9;
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.closePath(); ctx.fill();
    // 6 orbiting eyes (pulse with phase)
    for (let i = 0; i < 6; i++) {
      const ang = this.omegaEyeAngles[i] + t * 0.4;
      const ex = x + w * 0.5 + Math.cos(ang) * w * 0.28;
      const ey = y + h * 0.5 + Math.sin(ang) * h * 0.28;
      ctx.save(); ctx.shadowColor = phaseColor; ctx.shadowBlur = 18;
      ctx.fillStyle = phaseColor;
      ctx.beginPath(); ctx.arc(ex, ey, 8, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#000';
      ctx.beginPath(); ctx.arc(ex, ey, 3, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
    // Tentacles wafting outward
    ctx.strokeStyle = this.color; ctx.lineWidth = 6;
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2 + t * 0.3;
      const sx = x + w * 0.5 + Math.cos(ang) * w * 0.3;
      const sy = y + h * 0.5 + Math.sin(ang) * h * 0.3;
      const ex = x + w * 0.5 + Math.cos(ang) * w * 0.55;
      const ey = y + h * 0.5 + Math.sin(ang) * h * 0.55 + Math.sin(t * 2 + i) * 8;
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.quadraticCurveTo((sx + ex) / 2 + Math.sin(t + i) * 10, (sy + ey) / 2, ex, ey); ctx.stroke();
    }
  }

  // =================================================================

  drawSoldier(ctx, x, y) {
    ctx.fillStyle='#5AC8FA';ctx.fillRect(x+5,y+20,25,25);ctx.fillRect(x+8,y,20,20);
    ctx.fillStyle='#FF3B30';ctx.fillRect(x+12,y+6,5,5);ctx.fillRect(x+20,y+6,5,5);
    ctx.fillStyle='#3B8AC8';const legOffset=Math.sin(this.animFrame*Math.PI)*3;
    ctx.fillRect(x+8,y+42+legOffset,8,12);ctx.fillRect(x+20,y+42-legOffset,8,12);
  }

  drawTank(ctx, x, y) {
    ctx.fillStyle='#5AC8FA';ctx.fillRect(x,y+10,60,25);ctx.fillRect(x+15,y,30,15);
    ctx.fillStyle='#333';ctx.fillRect(x+45,y+5,25,8);
    ctx.fillStyle='#3B8AC8';ctx.fillRect(x-5,y+30,70,10);
    for(let i=0;i<7;i++){ctx.fillStyle='#222';ctx.fillRect(x+i*10-3+(this.animFrame*2)%10,y+32,6,6);}
  }

  drawHelicopter(ctx, x, y) {
    ctx.fillStyle='#5AC8FA';ctx.fillRect(x+10,y+10,50,20);ctx.fillStyle='#333';ctx.fillRect(x+5,y+12,15,15);
    ctx.fillStyle='#3B8AC8';ctx.fillRect(x+55,y+15,20,8);ctx.fillRect(x+70,y+5,5,25);
    ctx.fillStyle='#888';ctx.save();ctx.translate(x+35,y+5);ctx.rotate(Date.now()/50);ctx.fillRect(-30,-2,60,4);ctx.restore();
    ctx.fillStyle='#333';ctx.fillRect(x+15,y+28,12,4);
  }

  drawMech(ctx, x, y) {
    ctx.fillStyle='#3B8AC8';const legOffset=Math.sin(this.animFrame*Math.PI)*5;
    ctx.fillRect(x+5,y+45+legOffset,15,30);ctx.fillRect(x+30,y+45-legOffset,15,30);
    ctx.fillStyle='#5AC8FA';ctx.fillRect(x+5,y+20,40,30);ctx.fillStyle='#333';ctx.fillRect(x+12,y,26,22);
    ctx.fillStyle='#FF3B30';ctx.fillRect(x+15,y+5,20,8);
    ctx.fillStyle='#3B8AC8';ctx.fillRect(x-5,y+25,12,8);ctx.fillRect(x+43,y+25,12,8);
    ctx.fillStyle='#333';ctx.fillRect(x-10,y+27,8,4);ctx.fillRect(x+52,y+27,8,4);
  }

  drawDrone(ctx, x, y) {
    ctx.fillStyle='#9945FF';ctx.beginPath();ctx.arc(x+12,y+12,12,0,Math.PI*2);ctx.fill();
    ctx.fillStyle='#FF3B30';ctx.beginPath();ctx.arc(x+12,y+12,5,0,Math.PI*2);ctx.fill();
    ctx.fillStyle='#666';ctx.save();ctx.translate(x+12,y+12);ctx.rotate(Date.now()/30);ctx.fillRect(-15,-2,30,4);ctx.fillRect(-2,-15,4,30);ctx.restore();
  }

  drawGunship(ctx, x, y) {
    ctx.fillStyle='#9945FF';ctx.fillRect(x+5,y+8,70,25);ctx.fillStyle='#333';ctx.fillRect(x,y+12,20,18);
    ctx.fillStyle='#FF3B30';ctx.fillRect(x+3,y+15,12,10);ctx.fillStyle='#6B3FA0';ctx.fillRect(x+65,y+12,20,12);ctx.fillRect(x+80,y+2,5,35);
    ctx.fillStyle='#888';ctx.save();ctx.translate(x+25,y+3);ctx.rotate(Date.now()/40);ctx.fillRect(-25,-2,50,4);ctx.restore();
    ctx.save();ctx.translate(x+55,y+3);ctx.rotate(-Date.now()/40);ctx.fillRect(-20,-2,40,4);ctx.restore();
    ctx.fillStyle='#333';ctx.fillRect(x+15,y+32,15,6);ctx.fillRect(x+45,y+32,15,6);
  }

  drawGenericEnemy(ctx, x, y) {
    const color = this.color || '#5AC8FA';
    const darkerColor = this.themedSecondary || this.darkenColor(color, 30);
    const eyeColor = this.themedAccent || '#FF3B30';
    ctx.fillStyle = darkerColor;
    const legOffset = Math.sin(this.animFrame * Math.PI) * 4;
    ctx.fillRect(x+this.width*0.2,y+this.height*0.7+legOffset,this.width*0.2,this.height*0.35);
    ctx.fillRect(x+this.width*0.6,y+this.height*0.7-legOffset,this.width*0.2,this.height*0.35);
    ctx.fillStyle = color;
    ctx.fillRect(x+this.width*0.15,y+this.height*0.35,this.width*0.7,this.height*0.4);
    ctx.fillRect(x+this.width*0.25,y,this.width*0.5,this.height*0.38);
    ctx.fillStyle = eyeColor;
    ctx.fillRect(x+this.width*0.3,y+this.height*0.1,this.width*0.15,this.height*0.1);
    ctx.fillRect(x+this.width*0.55,y+this.height*0.1,this.width*0.15,this.height*0.1);
    ctx.fillStyle = darkerColor;
    ctx.fillRect(x+this.width*0.8,y+this.height*0.4,this.width*0.25,this.height*0.12);
    ctx.fillStyle = '#333';
    ctx.fillRect(x+this.width,y+this.height*0.42,this.width*0.2,this.height*0.08);
    if(this.type==='ninja'){ctx.fillStyle=eyeColor;ctx.fillRect(x+this.width*0.2,y+this.height*0.02,this.width*0.6,this.height*0.06);}
    else if(this.type==='berserker'){ctx.fillStyle='#FF3B30';for(let i=0;i<3;i++)ctx.fillRect(x+this.width*(0.3+i*0.15),y-this.height*0.1,this.width*0.1,this.height*0.12);}
    else if(this.type==='elite'){ctx.strokeStyle='#FFD700';ctx.lineWidth=2;ctx.strokeRect(x+this.width*0.15,y+this.height*0.35,this.width*0.7,this.height*0.4);}
    else if(this.type==='commando'){ctx.fillStyle='#9945FF';ctx.fillRect(x+this.width*0.2,y-this.height*0.05,this.width*0.6,this.height*0.08);}
    else if(this.type==='jumper'){ctx.fillStyle='#FF9500';ctx.fillRect(x+this.width*0.25,y+this.height*0.85,this.width*0.15,this.height*0.05);ctx.fillRect(x+this.width*0.6,y+this.height*0.85,this.width*0.15,this.height*0.05);}
    else if(this.type==='sniper'){
      // Long scope/rifle protruding forward + red laser sight when aiming
      ctx.fillStyle='#1a1a1a';
      const rifleDir = this.velocityX >= 0 ? 1 : -1;
      const rifleX = rifleDir > 0 ? x + this.width * 0.8 : x - this.width * 0.35;
      ctx.fillRect(rifleX, y + this.height * 0.4, this.width * 0.55, this.height * 0.08);
      // Scope
      ctx.fillStyle=eyeColor;
      ctx.fillRect(rifleX + this.width * 0.2, y + this.height * 0.35, this.width * 0.1, this.height * 0.06);
      if (this.isAiming) {
        // Red laser sight — pulsing red line extending far forward
        const laserY = y + this.height * 0.44;
        const laserStart = rifleX + (rifleDir > 0 ? this.width * 0.55 : 0);
        const laserEnd = laserStart + rifleDir * 600;
        ctx.save();
        ctx.strokeStyle = `rgba(255, 40, 40, ${0.4 + Math.sin(Date.now() / 60) * 0.3})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(laserStart, laserY);
        ctx.lineTo(laserEnd, laserY);
        ctx.stroke();
        ctx.restore();
      }
    }
    else if(this.type==='teleporter'){
      // Purple glyph orb hovering above head (visible even during fade)
      const orbPulse = 0.5 + Math.sin(Date.now() / 200) * 0.3;
      ctx.save();
      ctx.shadowColor = '#9945FF';
      ctx.shadowBlur = 12;
      ctx.fillStyle = `rgba(153, 69, 255, ${orbPulse})`;
      ctx.beginPath();
      ctx.arc(x + this.width * 0.5, y - this.height * 0.15, this.width * 0.15, 0, Math.PI * 2);
      ctx.fill();
      // Rune cross inside orb
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x + this.width * 0.5, y - this.height * 0.15 - this.width * 0.1);
      ctx.lineTo(x + this.width * 0.5, y - this.height * 0.15 + this.width * 0.1);
      ctx.moveTo(x + this.width * 0.5 - this.width * 0.1, y - this.height * 0.15);
      ctx.lineTo(x + this.width * 0.5 + this.width * 0.1, y - this.height * 0.15);
      ctx.stroke();
      ctx.restore();
    }
    else if(this.type==='brute'){
      // Massive shoulder pads
      ctx.fillStyle = darkerColor;
      ctx.fillRect(x + this.width * 0.05, y + this.height * 0.28, this.width * 0.2, this.height * 0.18);
      ctx.fillRect(x + this.width * 0.75, y + this.height * 0.28, this.width * 0.2, this.height * 0.18);
      // Telegraph glow during charge-prep phase
      if (this.chargePhase === 1) {
        ctx.save();
        const glowPulse = 0.4 + Math.sin(this.chargePhaseTimer * 0.5) * 0.4;
        ctx.shadowColor = '#FF3B30';
        ctx.shadowBlur = 25 * glowPulse;
        ctx.strokeStyle = `rgba(255, 59, 48, ${glowPulse})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(x - 3, y - 3, this.width + 6, this.height + 6);
        ctx.restore();
      } else if (this.chargePhase === 2) {
        // Motion trail during dash
        ctx.save();
        ctx.fillStyle = 'rgba(255, 59, 48, 0.35)';
        const trailDir = this.velocityX > 0 ? -1 : 1;
        for (let i = 1; i <= 3; i++) {
          ctx.globalAlpha = 0.35 - i * 0.09;
          ctx.fillRect(x + trailDir * i * 8, y, this.width, this.height);
        }
        ctx.restore();
      }
    }
  }

  darkenColor(color, amount) {
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - amount);
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - amount);
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  canShoot() {
    // Don't fire while underground — DUNE DEVOURER only bites during erupt phase
    if (this.type === 'boss_worm' && this.wormBurrowPhase === 2) return false;
    return this.shootCooldown <= 0;
  }
  shoot() { this.shootCooldown = this.shootRate; soundManager.playEnemyShoot(); }

  getScoreValue() {
    switch (this.type) {
      case 'tank': return 100;
      case 'helicopter': return 150;
      case 'mech': return 200;
      case 'drone': return 50;
      case 'boss': return 1000;
      default: return 50;
    }
  }
}

export default Enemy;
